import * as Dialog from '@radix-ui/react-dialog';
import { useEffect, useRef, useState, useCallback } from "react";
import { cn } from "../../@/lib/utils";
import { DxfCanvasViewerConfigProps } from './domain/types';
import { FiChevronDown, FiChevronUp, FiTarget, FiTag, FiSun, FiMoon, FiPlus, FiFile, FiUpload, FiFileMinus } from 'react-icons/fi';
import { HooksProps } from '../../types/common';
import getColor from './utils/getColor';
import { Entity, Point, ViewState, Stats, Comment, Label, Document } from './domain/types';
import parseText from './utils/parseText';

import { FaBorderAll, FaBorderNone } from 'react-icons/fa';
import { TbTableExport, TbTagOff } from 'react-icons/tb';
import { MdOutlineComment, MdOutlineCommentsDisabled, MdOutlineGrid4X4 } from 'react-icons/md';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import WidgetLayer from './components/WidgetLayer';
import AddWidgetModal from './components/AddWidgetModal';
import AddDocumentModal from './components/AddDocumentModal';
import DocumentLayer from './components/DocumentLayer';
import Select from 'react-select';
import { useDxfWidgets, WidgetType, WidgetConfig, DxfWidget } from './hooks/useDxfWidgets';
import { uploadFile, getFile } from 'api/fileManager';
import { getAssetDocs } from 'api/docs';
import { bootstrapStoredThemeColors, bootstrapStoredThemeMode, getStoredThemeMode, persistThemeMode } from 'utilities/themeColors';


bootstrapStoredThemeColors();
bootstrapStoredThemeMode();



const useBufferedCanvas = () => {
  const mainCanvasRef = useRef<HTMLCanvasElement>(null);
  const bufferRef = useRef<{ canvas: HTMLCanvasElement | null }>({ canvas: null });
  const [isBufferInitialized, setIsBufferInitialized] = useState(false);

  useEffect(() => {
    if (!bufferRef.current.canvas) {
      bufferRef.current.canvas = document.createElement('canvas');
      setIsBufferInitialized(true);
    }
  }, []);

  return { mainCanvasRef, bufferRef, isBufferInitialized };
};

interface Props { forceConfig?: boolean, dxfCanvasViewerConfig: DxfCanvasViewerConfigProps, hooks: HooksProps, colorTheme?: 'sdaia' | 'default' }
const DxfCanvasViewer: React.FC<Props> = ({ forceConfig = false, dxfCanvasViewerConfig, hooks, colorTheme = 'default' }) => {
  const addWidgetFnRef = useRef<((w: any) => void) | null>(null);
  const [pendingWidget, setPendingWidget] = useState<{ type: WidgetType; config: WidgetConfig } | null>(null);
  const pendingWidgetRef = useRef<{ type: WidgetType; config: WidgetConfig } | null>(null);
  
  // Keep ref in sync with state
  useEffect(() => {
    pendingWidgetRef.current = pendingWidget;
  }, [pendingWidget]);


    // Hydrate colors for the currently stored theme mode
    const mode = getStoredThemeMode();

  // THEME STATE
  const [theme, setTheme] = useState<'dark' | 'light'>(mode);


  const { mainCanvasRef: canvasRef, bufferRef, isBufferInitialized } = useBufferedCanvas();
  const fileRef = useRef<HTMLInputElement>(null);
  const [selectedFileName, setSelectedFileName] = useState<string>("");

  const { useApp } = hooks;
  const { getAllTags, getTagData, getAllExpressions, getExpressionData } = dxfCanvasViewerConfig;
  const { activeTabId, tabsData, updateTabData } = useApp();
  const { widgets, updateWidget, setCurrentZoom, clearAllWidgets, updateWidgetPosition } = useDxfWidgets();
  // Store API functions in refs to prevent useEffect re-runs
  const apiFunctionsRef = useRef({
    getTagData,
    getExpressionData
  });
  
  // Update refs when functions change
  useEffect(() => {
    apiFunctionsRef.current = {
      getTagData,
      getExpressionData
    };
  }, [getTagData, getExpressionData]);
  const currentTab: any = tabsData.find((t: any) => t.id === activeTabId);

  const hasDxfData = Boolean(currentTab?.data);
  const fileChosen = !!selectedFileName;

  const [comments, setComments] = useState<Comment[]>(currentTab?.comments || []);
  const [labels, setLabels] = useState<Label[]>(currentTab?.labels || []);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [availableDocuments, setAvailableDocuments] = useState<any[]>([]);
  const [documentModalMode, setDocumentModalMode] = useState<'select' | 'upload'>('select');
  const [selectedDocumentId, setSelectedDocumentId] = useState<string>('');
  const [isUploadingDocument, setIsUploadingDocument] = useState(false);
  
  // Click mode states for adding comments, sensors and documents
  const [clickMode, setClickMode] = useState<'none' | 'comment' | 'sensor' | 'document'>('none');
  const [newLabel, setNewLabel] = useState<{ x: number; y: number; id?: string; tagId?: number; expressionId?: number; screenX?: number; screenY?: number } | null>(null);
  const [newDocument, setNewDocument] = useState<{ x: number; y: number; id?: string; documentId?: number; screenX?: number; screenY?: number; file?: File } | null | any>(null);
  const [labelInput, setLabelInput] = useState("");
  const labelValue = "Value 1";
  const animationFrameRef = useRef<number | undefined>();
  const lastDrawTime = useRef<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(false);
  // const [isVisible, setIsVisible] = useState(true);
  const [newComment, setNewComment] = useState<{ x: number; y: number; id?: string; screenX?: number; screenY?: number } | null>(null);
  const [commentText, setCommentText] = useState("");
  const [isEditingComment, setIsEditingComment] = useState(false);
  const commentInputRef = useRef<HTMLTextAreaElement>(null);
  const commentModalRef = useRef<HTMLDivElement>(null);
  const labelModalRef = useRef<HTMLDivElement>(null);
  const documentModalRef = useRef<HTMLDivElement>(null);
  const isDrawing = useRef(false);
  const transformCache = useRef<Map<string, { x: number, y: number }>>(new Map());
  const documentsFetchedRef = useRef(false);

  const [selectedTagId, setSelectedTagId] = useState<number>(0);
  // Additional state for sensor labels
  const [selectedSource, setSelectedSource] = useState<'tag' | 'expression'>('tag');
  const [selectedExpressionId, setSelectedExpressionId] = useState<number>(0);
  const [tagsList, setTagsList] = useState<any[]>([]);
  const [exprList, setExprList] = useState<any[]>([]);
  const [forceRedraw, setForceRedraw] = useState(false);

  // Move these lines up, before the draw function:
  const [gridEnabled, setGridEnabled] = useState(false);
  const [gridSpacing, setGridSpacing] = useState(10);
  const [showGridSettings, setShowGridSettings] = useState(false);
  const [gridStyle, setGridStyle] = useState<'solid' | 'dotted'>('solid');

  // Add new state for action bar and toggles
  const [showActionBar, setShowActionBar] = useState(false);
  // Widget and document modal states
  const [addWidgetOpen, setAddWidgetOpen] = useState(false);
  const [addDocumentOpen, setAddDocumentOpen] = useState(false);
  const [editingWidget, setEditingWidget] = useState<DxfWidget | null>(null);
  const [showDocuments, setShowDocuments] = useState(true);
  const [isDocumentResizing, setIsDocumentResizing] = useState(false);
  const [documentResizingId, setDocumentResizingId] = useState<string | null>(null);
  
  // Refs for document persistence (similar to widgets)
  const lastSavedDocumentsRef = useRef<string>('');
  const pendingDocumentSaveRef = useRef<NodeJS.Timeout | null>(null);
  const lastDocumentSaveTimeRef = useRef<number>(0);
  const isModifyingDocumentRef = useRef(false);
  const documentsLoadedRef = useRef(false);
  
  // Toast notification state
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastVisible, setToastVisible] = useState(false);
  const [toastPersistent, setToastPersistent] = useState(false); // For persistent toasts
  
  // Function to show toast message
  const showToast = useCallback((message: string, duration: number = 4000, persistent: boolean = false) => {
    setToastMessage(message);
    setToastVisible(true);
    setToastPersistent(persistent);
    
    // Auto-hide after duration only if not persistent
    if (!persistent) {
      setTimeout(() => {
        setToastVisible(false);
        setTimeout(() => setToastMessage(null), 300); // Wait for fade out animation
      }, duration);
    }
  }, []);
  
  // Function to hide persistent toast
  const hideToast = useCallback(() => {
    setToastVisible(false);
    setToastPersistent(false);
    setTimeout(() => setToastMessage(null), 300);
  }, []);
  
  // Function to cancel widget placement
  const cancelWidgetPlacement = useCallback(() => {
    setPendingWidget(null);
    hideToast();
  }, [hideToast]);
  
  
  // Handle Escape key to cancel widget placement and click modes
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (pendingWidget) {
          cancelWidgetPlacement();
        } else if (clickMode !== 'none') {
          setClickMode('none');
          hideToast();
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [pendingWidget, cancelWidgetPlacement, clickMode]);
  const [showComments, setShowComments] = useState(true);
  const [showLabels, setShowLabels] = useState(true);
  const [showWidgets, setShowWidgets] = useState(true);
  const [actionBarAnimating, setActionBarAnimating] = useState(false);
  const [isWidgetResizing, setIsWidgetResizing] = useState(false);
  const [widgetResizingId, setWidgetResizingId] = useState<string | null>(null);
  // Define labelsRef to keep latest labels
  const labelsRef = useRef<Label[]>([]);

  useEffect(() => {
    (async () => {
      try {
        const resTags: any = await getAllTags();
        if (resTags.status === 200) setTagsList(resTags.data);
      } catch (err) {
        // Handle error silently
      }
      try {
        const resExpr: any = await getAllExpressions();
        if (resExpr.status === 200) setExprList(resExpr.data);
      } catch (err) {
        // Handle error silently
      }
    })();
  }, []);

  // View state
  const viewState = useRef<ViewState>({
    scale: 1,
    offsetX: 0,
    offsetY: 0,
    isDragging: false,
    lastX: 0,
    lastY: 0,
    entities: [],
    baseScale: 1,
    bounds: {
      minX: 0,
      minY: 0,
      maxX: 0,
      maxY: 0
    }
  });

  const [hoveredCommentId, setHoveredCommentId] = useState<string | null>(null);
  const [hoveredLabelId, setHoveredLabelId] = useState<string | null>(null);
  const [isEditingLabel, setIsEditingLabel] = useState(false);
  const [itemActionMenu, setItemActionMenu] = useState<{ type: 'comment' | 'label' | 'widget' | 'document'; id: string; screenX: number; screenY: number } | null>(null);
  const [movingItem, setMovingItem] = useState<{ type: 'comment' | 'label' | 'widget' | 'document'; id: string } | null>(null);
  const itemActionRef = useRef<HTMLDivElement | null>(null);
  const [resizingItem, setResizingItem] = useState<{ type: 'comment' | 'label' | 'widget' | 'document'; id: string; startX: number; initialScale: number } | null>(null);
  // Ref to track current position during move operations to avoid excessive state updates
  const movePositionRef = useRef<{ x: number; y: number } | null>(null);
  // Ref to track if component is mounted and ready for persistence
  const isMountedRef = useRef(false);

  // Load saved data & comments from current tab whenever active tab or tabsData change
  useEffect(() => {
    if (!currentTab || currentTab.type !== 'dxf-viewer') {
      isMountedRef.current = false;
      documentsLoadedRef.current = false;
      // Clear comments and labels when switching away from DXF tabs
      setComments([]);
      setLabels([]);
      labelsRef.current = [];
      setDocuments([]); // Clear documents when switching away from DXF tabs
      // Clear any temporary states
      setNewComment(null);
      setNewLabel(null);
      setItemActionMenu(null);
      setMovingItem(null);
      setResizingItem(null);
      return;
    }

    // Set mounted flag when we have a valid DXF tab
    isMountedRef.current = true;

    // Load comments and labels for this specific tab
    const tabComments = currentTab.comments || [];
    const tabLabels = currentTab.labels || [];
    
    setComments(tabComments);
    setLabels(tabLabels);
    labelsRef.current = tabLabels;

    // Load documents for this specific tab (similar to widgets)
    const tabDocuments = currentTab.documents || [];
    
    if (tabDocuments.length > 0) {
      // Mark as not loaded yet
      documentsLoadedRef.current = false;
      
      // Set documents without files first (similar to widgets loading)
      setDocuments(tabDocuments);
      
      // Fetch file content asynchronously
      const fetchDocumentFiles = async () => {
        try {
          const documentsWithFiles = await Promise.all(
            tabDocuments.map(async (doc: any) => {
              if (doc.fileId) {
                try {
                  const response = await getFile(doc.fileId);
                  if (response.status === 200) {
                    // Convert base64 data to Uint8Array
                    const base64Data = response.data.data || response.data;
                    const binaryString = atob(base64Data);
                    const bytes = new Uint8Array(binaryString.length);
                    for (let i = 0; i < binaryString.length; i++) {
                      bytes[i] = binaryString.charCodeAt(i);
                    }
                    
                    // Create blob and file from the decoded data
                    const blob = new Blob([bytes], { type: response.data.mimeType || 'application/pdf' });
                    const file = new File([blob], response.data.fileName || doc.name, { type: response.data.mimeType || 'application/pdf' });

                    return {
                      ...doc,
                      file
                    };
                  }
                } catch (error) {
                  // If file fetch fails, return document without file
                  return doc;
                }
              }
              return doc;
            })
          );
          
          // Mark as loaded to prevent re-triggering persistence
          documentsLoadedRef.current = true;
          setDocuments(documentsWithFiles);
          
          // Update the last saved reference to prevent immediate re-save
          const documentsForPersistence = documentsWithFiles.map(doc => ({
            id: doc.id,
            x: doc.x,
            y: doc.y,
            name: doc.name,
            fileId: doc.fileId,
            createdAt: doc.createdAt,
            scale: doc.scale,
            fixedSize: doc.fixedSize
          }));
          lastSavedDocumentsRef.current = JSON.stringify(documentsForPersistence);
        } catch (error) {
          // Handle error silently
          documentsLoadedRef.current = true;
        }
      };
      
      fetchDocumentFiles();
    } else {
      setDocuments([]);
      documentsLoadedRef.current = true;
      lastSavedDocumentsRef.current = JSON.stringify([]);
    }

    // Load DXF entities
    if (currentTab.data) {
      try {
        const parsed = JSON.parse(currentTab.data);
        processEntities(parsed);
      } catch (err) {
        // Handle error silently
      }
    }

    // Ensure a redraw happens after all data is loaded
    requestAnimationFrame(() => {
      draw();
    });
  }, [currentTab?.id]); // Only depend on tab ID to prevent unnecessary re-runs

  // Persist comments to backend whenever they change
  useEffect(() => {
    if (!currentTab || currentTab.type !== 'dxf-viewer') return;
    if ((movingItem && movingItem.type === 'comment') || (resizingItem && resizingItem.type === 'comment')) return; // skip while moving or resizing comment

    // Don't persist if we're not on a DXF viewer tab or if the tab doesn't have DXF data
    if (!currentTab.data) return;

    // Don't persist if component is not properly mounted
    if (!isMountedRef.current) return;

    // Don't persist if we're not on the correct tab (prevent persisting data from wrong tab)
    if (currentTab.id !== activeTabId) return;

    const storedJson = JSON.stringify(currentTab.comments || []);
    const newJson = JSON.stringify(comments);
    if (storedJson === newJson) return;

    updateTabData(activeTabId, { comments });
  }, [comments, currentTab?.id, activeTabId, updateTabData, movingItem, resizingItem]);

  // INSERT NEW EFFECT TO PERSIST LABELS
  useEffect(() => {
    if (!currentTab || currentTab.type !== 'dxf-viewer') return;
    if ((movingItem && movingItem.type === 'label') || (resizingItem && resizingItem.type === 'label')) return; // skip while moving or resizing label

    // Don't persist if we're not on a DXF viewer tab or if the tab doesn't have DXF data
    if (!currentTab.data) return;

    // Don't persist if component is not properly mounted
    if (!isMountedRef.current) return;

    // Don't persist if we're not on the correct tab (prevent persisting data from wrong tab)
    if (currentTab.id !== activeTabId) return;

    const stored = JSON.stringify(currentTab.labels || []);
    const latest = JSON.stringify(labels);
    if (stored === latest) return;
    updateTabData(activeTabId, { labels });
  }, [labels, currentTab?.id, activeTabId, updateTabData, movingItem, resizingItem]);

  // PERSIST DOCUMENTS TO BACKEND (similar to widgets with debouncing)
  const saveDocumentsToBackend = useCallback(() => {
    console.log('Saving documents to backend...');
    if (!activeTabId) return;
    
    const currentTab = tabsData.find((tab: any) => tab && tab.id === activeTabId);
    if (!currentTab || currentTab.type !== 'dxf-viewer') return;

    // Don't persist if component is not properly mounted or documents not loaded yet
    if (!isMountedRef.current || !documentsLoadedRef.current) return;

    // Create a version of documents without the file data for persistence
    const documentsForPersistence = documents.map(doc => ({
      id: doc.id,
      x: doc.x,
      y: doc.y,
      name: doc.name,
      fileId: doc.fileId,
      createdAt: doc.createdAt,
      scale: doc.scale,
      fixedSize: doc.fixedSize
    }));

    const newJson = JSON.stringify(documentsForPersistence);
    
    // Compare with last saved to avoid unnecessary updates
    if (lastSavedDocumentsRef.current === newJson) return;

    try {
      updateTabData(activeTabId, { documents: documentsForPersistence });
      lastSavedDocumentsRef.current = newJson;
    } catch (error) {
      // Handle save error silently
    }
  }, [documents, activeTabId, tabsData, updateTabData]);

  useEffect(() => {
    // Skip if no documents or not on active tab
    if (!activeTabId || !documentsLoadedRef.current) return;

    // Skip saving while moving or resizing to avoid excessive API calls
    if (isModifyingDocumentRef.current) return;
    if ((movingItem && movingItem.type === 'document') || (resizingItem && resizingItem.type === 'document')) return;

    // Clear any pending save
    if (pendingDocumentSaveRef.current) {
      clearTimeout(pendingDocumentSaveRef.current);
    }

    // Check if we've saved recently (within 2 seconds)
    const now = Date.now();
    const timeSinceLastSave = now - lastDocumentSaveTimeRef.current;

    if (timeSinceLastSave < 2000) {
      // If we've saved recently, schedule a save after a delay
      pendingDocumentSaveRef.current = setTimeout(() => {
        saveDocumentsToBackend();
        lastDocumentSaveTimeRef.current = Date.now();
        pendingDocumentSaveRef.current = null;
      }, 2000);
    } else {
      // If we haven't saved recently, save immediately
      saveDocumentsToBackend();
      lastDocumentSaveTimeRef.current = now;
    }

    // Clean up any pending save on unmount
    return () => {
      if (pendingDocumentSaveRef.current) {
        clearTimeout(pendingDocumentSaveRef.current);
      }
    };
  }, [documents, activeTabId, saveDocumentsToBackend, movingItem, resizingItem]);

  // Save view state when it changes (still local per browser)
  const saveViewState = useCallback(() => {
    try {
      const stateToSave = {
        scale: viewState.current.scale,
        offsetX: viewState.current.offsetX,
        offsetY: viewState.current.offsetY,
        baseScale: viewState.current.baseScale,
        bounds: viewState.current.bounds
      };
      localStorage.setItem('dxfViewerViewState', JSON.stringify(stateToSave));
    } catch (error) {
      // Handle error silently
    }
  }, []);

  // Add debounced save view state to relevant handlers
  useEffect(() => {
    const debouncedSave = debounce(saveViewState, 500);
    return () => debouncedSave.cancel();
  }, [saveViewState]);

  // Add debounce utility function with proper typing
  const debounce = <T extends (...args: any[]) => any>(func: T, wait: number) => {
    let timeout: NodeJS.Timeout;
    const debouncedFunc = (...args: Parameters<T>) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
    debouncedFunc.cancel = () => clearTimeout(timeout);
    return debouncedFunc;
  };

  // Function to convert canvas coordinates to world coordinates
  const canvasToWorld = useCallback((canvasX: number, canvasY: number) => {
    const view = viewState.current;
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    // Check if view state is properly initialized
    if (!view.bounds || typeof view.bounds.minX !== 'number' || typeof view.bounds.minY !== 'number') {
      return { x: 0, y: 0 };
    }

    // Convert screen coordinates to world coordinates
    const worldX = (canvasX - view.offsetX) / (view.baseScale * view.scale) + view.bounds.minX;
    const worldY = (canvas.height - canvasY - view.offsetY) / (view.baseScale * view.scale) + view.bounds.minY;
    return { x: worldX, y: worldY };
  }, []);

  // Function to convert world coordinates to canvas coordinates
  const worldToCanvas = useCallback((worldX: number, worldY: number) => {
    const view = viewState.current;
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    // Check if view state is properly initialized
    if (!view.bounds || typeof view.bounds.minX !== 'number' || typeof view.bounds.minY !== 'number') {
      return { x: 0, y: 0 };
    }

    // Convert world coordinates to screen coordinates
    const canvasX = ((worldX - view.bounds.minX) * view.baseScale * view.scale) + view.offsetX;
    const canvasY = canvas.height - (((worldY - view.bounds.minY) * view.baseScale * view.scale) + view.offsetY);
    return { x: canvasX, y: canvasY };
  }, []);

  // Grid snapping function
  const snapToGrid = (worldX: number, worldY: number): { x: number; y: number } => {
    if (!gridEnabled) return { x: worldX, y: worldY };
    
    const snappedX = Math.round(worldX / gridSpacing) * gridSpacing;
    const snappedY = Math.round(worldY / gridSpacing) * gridSpacing;
    
    return { x: snappedX, y: snappedY };
  };

  // Draw grid function
  const drawGrid = (ctx: CanvasRenderingContext2D) => {
    if (!gridEnabled) return;
    
    const view = viewState.current;
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Check if view bounds are initialized
    if (!view.bounds || typeof view.bounds.minX !== 'number' || typeof view.bounds.minY !== 'number') {
      return;
    }

    // Calculate visible area
    const visibleWorldBounds = {
      minX: (0 - view.offsetX) / (view.baseScale * view.scale) + view.bounds.minX,
      minY: (0 - view.offsetY) / (view.baseScale * view.scale) + view.bounds.minY,
      maxX: (canvas.width - view.offsetX) / (view.baseScale * view.scale) + view.bounds.minX,
      maxY: (canvas.height - view.offsetY) / (view.baseScale * view.scale) + view.bounds.minY
    };

    // Snap bounds to grid
    const gridMinX = Math.floor(visibleWorldBounds.minX / gridSpacing) * gridSpacing;
    const gridMinY = Math.floor(visibleWorldBounds.minY / gridSpacing) * gridSpacing;
    const gridMaxX = Math.ceil(visibleWorldBounds.maxX / gridSpacing) * gridSpacing;
    const gridMaxY = Math.ceil(visibleWorldBounds.maxY / gridSpacing) * gridSpacing;

    ctx.save();
    ctx.globalCompositeOperation = 'source-over';

    // --- PERFORMANCE OPTIMIZATION ---
    // Calculate grid spacing in screen pixels
    const screenGridSpacing = gridSpacing * view.baseScale * view.scale;
    // Calculate how many lines/dots would be drawn
    const numVertical = Math.ceil((gridMaxX - gridMinX) / gridSpacing) + 1;
    const numHorizontal = Math.ceil((gridMaxY - gridMinY) / gridSpacing) + 1;
    const maxGridLines = 200;
    let xStep = Math.ceil(numVertical / maxGridLines);
    let yStep = Math.ceil(numHorizontal / maxGridLines);

    // If grid spacing is too small, draw a coarse grid instead of skipping
    let coarseMode = false;
    let coarseStep = 1;
    if (screenGridSpacing < 8) {
      coarseMode = true;
      coarseStep = Math.ceil(8 / screenGridSpacing);
      xStep = Math.max(xStep, coarseStep);
      yStep = Math.max(yStep, coarseStep);
    }

    if (gridStyle === 'solid') {
      ctx.strokeStyle = coarseMode ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255, 255, 255, 0.15)';
      ctx.lineWidth = 1;
    // Draw vertical lines
    ctx.beginPath();
      for (let x = gridMinX, i = 0; x <= gridMaxX; x += gridSpacing, i++) {
        if (xStep > 1 && i % xStep !== 0) continue;
      const canvasX = ((x - view.bounds.minX) * view.baseScale * view.scale) + view.offsetX;
      if (canvasX >= 0 && canvasX <= canvas.width) {
        ctx.moveTo(canvasX, 0);
        ctx.lineTo(canvasX, canvas.height);
      }
    }
    ctx.stroke();
    // Draw horizontal lines
    ctx.beginPath();
      for (let y = gridMinY, j = 0; y <= gridMaxY; y += gridSpacing, j++) {
        if (yStep > 1 && j % yStep !== 0) continue;
      const canvasY = canvas.height - (((y - view.bounds.minY) * view.baseScale * view.scale) + view.offsetY);
      if (canvasY >= 0 && canvasY <= canvas.height) {
        ctx.moveTo(0, canvasY);
        ctx.lineTo(canvas.width, canvasY);
      }
    }
    ctx.stroke();
    } else if (gridStyle === 'dotted') {
      ctx.fillStyle = coarseMode ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.12)';
      const dotRadius = 1.2 * Math.max(1, view.scale * 0.8);
      for (let x = gridMinX, i = 0; x <= gridMaxX; x += gridSpacing, i++) {
        if (xStep > 1 && i % xStep !== 0) continue;
        for (let y = gridMinY, j = 0; y <= gridMaxY; y += gridSpacing, j++) {
          if (yStep > 1 && j % yStep !== 0) continue;
          const canvasX = ((x - view.bounds.minX) * view.baseScale * view.scale) + view.offsetX;
          const canvasY = canvas.height - (((y - view.bounds.minY) * view.baseScale * view.scale) + view.offsetY);
          if (canvasX >= 0 && canvasX <= canvas.width && canvasY >= 0 && canvasY <= canvas.height) {
            ctx.beginPath();
            ctx.arc(canvasX, canvasY, dotRadius, 0, 2 * Math.PI);
            ctx.fill();
          }
        }
      }
    }
    ctx.restore();
  };

  // Helper function to wrap text to fit within a maximum width
  const wrapText = (ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] => {
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = words[0] || '';

    for (let i = 1; i < words.length; i++) {
      const word = words[i];
      const width = ctx.measureText(currentLine + ' ' + word).width;
      if (width < maxWidth) {
        currentLine += ' ' + word;
      } else {
        lines.push(currentLine);
        currentLine = word;
      }
    }
    lines.push(currentLine);
    return lines;
  };

  // Helper function to get CSS variable color values
  const getCSSColor = (varName: string): string => {
    if (typeof window === 'undefined' || !document.documentElement) {
      return '';
    }
    return getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
  };

  // Helper to draw rounded rectangles (used in drawComments)
  function roundedRect(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    radius: number
  ) {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  }

  // Function to handle clicking on a document's controls
  const handleDocumentMenuClick = useCallback((e: MouseEvent, doc: Document) => {
    e.preventDefault(); // Prevent canvas pan
    e.stopPropagation(); // Prevent other handlers

    // Get the exact click position for the menu
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const pos = worldToCanvas(doc.x, doc.y);
    
    // Position the menu slightly offset from the document position
    setItemActionMenu({
      type: 'document',
      id: doc.id,
      screenX: pos.x + 20,
      screenY: pos.y - 10
    });
  }, [worldToCanvas]);

  // Add mouse move handler for cursor changes
  const handleCanvasMouseMove = useCallback((e: MouseEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // If in resize mode, adjust cursor accordingly first
    if (resizingItem) {
      if (!Number.isNaN(resizingItem.startX)) {
        canvas.style.cursor = 'sw-resize';
      } else {
        // Hover detection over resize handle of current item
        let overHandle = false;
        if (resizingItem.type === 'comment') {
          const c = comments.find(c => c.id === resizingItem.id);
          const area = (c as any)?.resizeArea;
          if (area) overHandle = pointInRect(x, y, area);
        } else {
          const l = labels.find(l => l.id === resizingItem.id);
          const area = (l as any)?.resizeArea;
          if (area) overHandle = pointInRect(x, y, area);
        }
        canvas.style.cursor = overHandle ? 'ne-resize' : 'default';
      }
      // We do not want normal hover logic when in resize mode
      return;
    }

    // Determine if hovering dot icon of any comment/label
    const overDot = comments.some(c => {
      const d = (c as any).dotArea;
      return d && x >= d.x && x <= d.x + d.width && y >= d.y && y <= d.y + d.height;
    }) || labels.some(l => {
      const d = (l as any).dotArea;
      return d && x >= d.x && x <= d.x + d.width && y >= d.y && y <= d.y + d.height;
    });

    // Hover comment/label body for possible edit
    const hoveredComment = comments.find(comment => {
      const hit = (comment as any).hitArea;
      if (!hit) return false;
      return x >= hit.x && x <= hit.x + hit.width && y >= hit.y && y <= hit.y + hit.height;
    });
    setHoveredCommentId(hoveredComment?.id || null);

    const hoveredLabel = labels.find(lbl => {
      const hit = (lbl as any).hitArea;
      if (!hit) return false;
      return x >= hit.x && x <= hit.x + hit.width && y >= hit.y && y <= hit.y + hit.height;
    });
    setHoveredLabelId(hoveredLabel?.id || null);

    // Update cursor: priority order -> overDot icon : pointer; dragging: grabbing; default grab
    if (overDot) {
      canvas.style.cursor = 'pointer';
    } else if (viewState.current.isDragging) {
      canvas.style.cursor = 'grabbing';
    } else {
      canvas.style.cursor = 'grab';
    }
    
  }, [comments, labels, worldToCanvas, resizingItem]);

  // Enhanced comment rendering with theme support
  const drawComments = useCallback((ctx: CanvasRenderingContext2D, theme: 'dark' | 'light') => {
    if (!showComments) return;
    const view = viewState.current;
    const canvas = canvasRef.current;
    if (!canvas || !comments || comments.length === 0) return;

    comments.forEach(comment => {
      if (!comment || typeof comment.x !== 'number' || typeof comment.y !== 'number') {
        return;
      }

      try {
        // Use temporary position if available (during move operations)
        const useX = (comment as any).tempX !== undefined ? (comment as any).tempX : comment.x;
        const useY = (comment as any).tempY !== undefined ? (comment as any).tempY : comment.y;
        
        // Convert world coordinates to canvas coordinates
        const pos = worldToCanvas(useX, useY);
        
        // Check if position is valid
        if (!pos || typeof pos.x !== 'number' || typeof pos.y !== 'number') {
          return;
        }

        // For fixedSize: maintain constant pixel size regardless of zoom
        const baseScale = comment.scale ?? 1;
        let scaleFactor: number;
        if (comment.fixedSize) {
          // Fixed size behavior: maintain constant pixel size regardless of zoom
          // Use direct scaling to maintain pixel size like widgets
          scaleFactor = baseScale * view.scale;
        } else {
          // Normal behavior: scale with zoom (smaller when zoom out, larger when zoom in)
          // Use direct scaling like widgets
          scaleFactor = baseScale * view.scale;
        }

        // Calculate comment marker area for hit testing
        const markerRadius = 3.5 * scaleFactor;
        const hitArea = {
          x: pos.x - markerRadius,
          y: pos.y - markerRadius,
          width: markerRadius * 2,
          height: markerRadius * 2
        };

        // Store hit area for later use
        (comment as any).hitArea = hitArea;

        // Draw comment marker
        ctx.beginPath();
        ctx.arc(pos.x, pos.y, markerRadius, 0, 2 * Math.PI);
        ctx.fillStyle = comment.id === hoveredCommentId ? getCSSColor('--color-lightGreen') : getCSSColor('--color-green');
        ctx.fill();

        // Draw border
        ctx.strokeStyle = theme === 'dark' ? '#1B1B1B' : '#E5E7EB';
        ctx.lineWidth = comment.id === hoveredCommentId ? 1.5 * scaleFactor : 1 * scaleFactor;
        ctx.stroke();

        // Draw comment text bubble
        const text = comment.text || '';
        // Allow font to scale with zoom (smaller when zoom out, larger when zoom in)
        const fontSize = 11 * scaleFactor;
        ctx.font = `${fontSize}px Arial`;
        
        // Split text into lines and calculate dimensions
        const maxWidth = 200 * scaleFactor; // Maximum width for text wrapping
        const lines = wrapText(ctx, text, maxWidth);
        const lineHeight = fontSize * 1.2;
        const padding = 6 * scaleFactor;
        
        // Calculate bubble dimensions
        let maxLineWidth = 0;
        lines.forEach((line: string) => {
          const lineWidth = ctx.measureText(line).width;
          maxLineWidth = Math.max(maxLineWidth, lineWidth);
        });
        
        const textWidth = maxLineWidth + padding * 2;
        // reserve space for menu icon on right
        const preIconSize = 6 * scaleFactor;
        const preIconPadding = 2 * scaleFactor;
        const bubbleWidthPre = textWidth + preIconSize + preIconPadding;
        const textHeight = lines.length * lineHeight;
        const bubbleHeight = textHeight + padding * 2;

        // Draw bubble background with theme-based colors
        ctx.fillStyle = theme === 'dark' ? '#1B1B1B' : '#FFFFFF';
        roundedRect(
          ctx,
          pos.x + 8 * scaleFactor,
          pos.y - bubbleHeight / 2,
          bubbleWidthPre,
          bubbleHeight,
          4 * scaleFactor
        );
        ctx.fill();

        // Draw subtle border with theme-based colors
        ctx.strokeStyle = theme === 'dark' ? '#2D2D2D' : '#D1D5DB';
        ctx.lineWidth = 1;
        ctx.stroke();

        // Draw multi-line text with theme-based colors
        ctx.fillStyle = theme === 'dark' ? '#FFFFFF' : '#082A43';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        lines.forEach((line: string, index: number) => {
          ctx.fillText(
            line,
            pos.x + 8 * scaleFactor + padding,
            pos.y - bubbleHeight / 2 + padding + index * lineHeight
          );
        });

        const isResizingThis = resizingItem && resizingItem.type === 'comment' && resizingItem.id === comment.id;
        if (!isResizingThis) {
          // Draw three-dot menu icon with theme-based colors
          const iconX = pos.x + 7 * scaleFactor + textWidth + preIconPadding;
          const iconY = pos.y - bubbleHeight / 2 + 3 * scaleFactor;
          const r = preIconSize / 5;
          ctx.fillStyle = theme === 'dark' ? '#FFFFFF' : '#082A43';
          for (let i = 0; i < 3; i++) {
            ctx.beginPath();
            ctx.arc(iconX + preIconSize / 2, iconY + r + i * r * 2.2, r, 0, 2 * Math.PI);
            ctx.fill();
          }
          (comment as any).dotArea = { x: iconX, y: iconY, width: preIconSize + 4, height: preIconSize + 4 };
        } else {
          // Draw resize handle as a rotated caret (arrow) icon, top-right, pointing to top-right (45deg)
          const resizeS = 14 * scaleFactor;
          const resizeX = pos.x + 15 * scaleFactor + bubbleWidthPre - resizeS;
          const resizeY = pos.y - 8 * scaleFactor - bubbleHeight / 2;
          ctx.save();
          ctx.translate(resizeX + resizeS / 2, resizeY + resizeS / 2);
          ctx.rotate(-Math.PI / 1.3); // 45deg counterclockwise
          ctx.beginPath();
          ctx.moveTo(-resizeS / 3, 0);
          ctx.lineTo(0, resizeS / 3);
          ctx.lineTo(resizeS / 3, 0);
          ctx.lineWidth = 2 * scaleFactor;
          ctx.strokeStyle = theme === 'dark' ? '#fff' : '#111';
          ctx.stroke();
          ctx.restore();
          // store area for interaction detection
          (comment as any).resizeArea = { x: resizeX, y: resizeY, width: resizeS, height: resizeS };
        }
      } catch (error) {
        // Handle drawing error silently
      }
    });
  }, [comments, worldToCanvas, hoveredCommentId, resizingItem, showComments, theme]);

  // Add canvas click handler for comment interaction
  const handleCanvasClick = useCallback((e: MouseEvent) => {
    // Close the full document modal if it's open
    setAddDocumentOpen(false);
    
    // If currently in move mode, a click should finalize the move (stop movingItem) and do nothing else
    if (movingItem) {
      // Finalize the move by updating the actual state with the final position
      if (movePositionRef.current && movePositionRef.current.x !== undefined && movePositionRef.current.y !== undefined) {
        // Store the position in local variables to avoid null access during map operations
        const finalX = movePositionRef.current.x;
        const finalY = movePositionRef.current.y;
        
        if (movingItem.type === 'comment') {
          setComments((prev) =>
            prev.map((c) => (c.id === movingItem.id ? { ...c, x: finalX, y: finalY } : c)),
          );
        } else {
          setLabels((prev) =>
            prev.map((l) => (l.id === movingItem.id ? { ...l, x: finalX, y: finalY } : l)),
          );
        }
      }
      
      // Clear temporary positions
      if (movingItem.type === 'comment') {
        const comment = comments.find(c => c.id === movingItem.id);
        if (comment) {
          delete (comment as any).tempX;
          delete (comment as any).tempY;
        }
      } else {
        const label = labels.find(l => l.id === movingItem.id);
        if (label) {
          delete (label as any).tempX;
          delete (label as any).tempY;
        }
      }
      
      setMovingItem(null);
      movePositionRef.current = null;
      draw();
      return;
    }

    // Check if view state is properly initialized before allowing interactions
    const view = viewState.current;
    if (!view.bounds || typeof view.bounds.minX !== 'number' || typeof view.bounds.minY !== 'number') {
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Handle click modes for adding comments and sensors
    if (clickMode === 'comment') {
      const worldPos = canvasToWorld(x, y);
      const snappedPos = snapToGrid(worldPos.x, worldPos.y);
      setNewComment({
        x: snappedPos.x,
        y: snappedPos.y,
        screenX: x,
        screenY: y,
      });
      setCommentText("");
      setIsEditingComment(false);
      setClickMode('none');
      hideToast();
      return;
    }

    if (clickMode === 'sensor') {
      const worldPos = canvasToWorld(x, y);
      const snappedPos = snapToGrid(worldPos.x, worldPos.y);
      setNewLabel({ 
        x: snappedPos.x, 
        y: snappedPos.y, 
        screenX: x, 
        screenY: y 
      });
      setSelectedSource('tag');
      const defTag = tagsList[0]?.id ?? 0;
      setSelectedTagId(defTag);
      setSelectedExpressionId(exprList[0]?.id ?? 0);
      setClickMode('none');
      hideToast();
      return;
    }

      if (clickMode === 'document') {
      const worldPos = canvasToWorld(x, y);
      const snappedPos = snapToGrid(worldPos.x, worldPos.y);
      setNewDocument({
        x: snappedPos.x,
        y: snappedPos.y,
        screenX: x,
        screenY: y
      });
      setClickMode('none');
      hideToast();
      return;
    }    // If a widget is waiting to be placed
    if (pendingWidgetRef.current && addWidgetFnRef.current) {
      const worldPos = canvasToWorld(x, y);
      addWidgetFnRef.current({ ...pendingWidgetRef.current, x: worldPos.x, y: worldPos.y });
      setPendingWidget(null);
      hideToast();
      return;
    }

    // First, check if click is on a comment's action icon
    const commentAction = comments.find(c => {
      const dot = (c as any).dotArea;
      return dot && x >= dot.x && x <= dot.x + dot.width && y >= dot.y && y <= dot.y + dot.height;
    });
    if (commentAction) {
      setItemActionMenu({ type: 'comment', id: commentAction.id, screenX: x, screenY: y });
      return;
    }

    // Next, check if click is on a label's action icon
    const labelAction = labels.find(l => {
      const dot = (l as any).dotArea;
      return dot && x >= dot.x && x <= dot.x + dot.width && y >= dot.y && y <= dot.y + dot.height;
    });
    if (labelAction) {
      setItemActionMenu({ type: 'label', id: labelAction.id, screenX: x, screenY: y });
      return;
    }

    // Direct clicks on comment markers or label headers are ignored (edit via action menu only)
  }, [comments, labels, movingItem, clickMode, canvasToWorld, snapToGrid, setNewComment, setCommentText, setIsEditingComment, setNewLabel, setSelectedSource, setSelectedTagId, setSelectedExpressionId, tagsList, exprList, hideToast]); // Added click mode dependencies

  // Enhanced text rendering function
  const drawText = (ctx: CanvasRenderingContext2D, entity: Entity, view: ViewState, theme: 'dark' | 'light') => {
    if (!entity.insertionPoint && !entity.position && !entity.startPoint) {
      return;
    }

    // Get the insertion point
    const position = entity.insertionPoint || entity.position || entity.startPoint!;

    // Transform function for converting world coordinates to screen coordinates
    const transform = (point: Point) => ({
      x: ((point.x - view.bounds.minX) * view.baseScale * view.scale) + view.offsetX,
      y: ctx.canvas.height - (((point.y - view.bounds.minY) * view.baseScale * view.scale) + view.offsetY)
    });

    const pos = transform(position);
    const rawText = entity.text || entity._code1text || '';

    // Parse text and get formatting parameters
    const { text: cleanText, height: parsedHeight } = parseText(rawText);
    if (!cleanText) {
      return;
    }

    // Handle different text entity scenarios based on object characteristics:
    // Working objects: have height codes in text (like \H3.5;) or reasonable textHeight
    // Problematic objects: have very small textHeight values that scale too large
    let textHeight: number;
    if (entity.type === 'MTEXT') {
      // Check if this is a working MTEXT object (has height codes in text)
      const hasHeightCodes = rawText.includes('\\H') && parsedHeight > 0;
      
      if (hasHeightCodes) {
        // Case 1: Working MTEXT objects - use parsed height (don't disturb)
        textHeight = parsedHeight;
      } else if (entity.height && entity.height > 0) {
        // Case 2: Problematic MTEXT objects - use very small fixed scaling
        // Specifically for objects like: height: 0.2, no height codes
        textHeight = entity.height * 0.5; // Scale 0.2 -> 0.1 (very small)
      } else {
        // Fallback
        textHeight = 2.5;
      }
    } else if (entity.type === 'TEXT') {
      // Handle TEXT entities with small textHeight values
      if (entity.textHeight && entity.textHeight < 1) {
        // Case: TEXT entities with very small textHeight (like 0.1125)
        textHeight = entity.textHeight * 0.5; // Scale 0.1125 -> 0.03375 (very small)
      } else {
        // Case: TEXT entities with reasonable textHeight or use parsed height
        textHeight = parsedHeight;
      }
    } else {
      // For other text types, use parsed height
      textHeight = parsedHeight;
    }

    // Use the determined text height
    const fontSize = textHeight * view.baseScale * view.scale;

    ctx.save();
    ctx.font = `${fontSize}px Arial`;
    ctx.fillStyle = theme === 'dark' ? '#fff' : '#111';
    ctx.textAlign = entity.attachmentPoint === 1 ? 'left' : 'center';
    ctx.textBaseline = 'middle';
    if (entity.rotation) {
      ctx.translate(pos.x, pos.y);
      ctx.rotate(-entity.rotation * Math.PI / 180);
      ctx.fillText(cleanText, 0, 0);
    } else {
      ctx.fillText(cleanText, pos.x, pos.y);
    }
    ctx.restore();
  };

   // Replace existing drawLabels arrow/useCallback/function with hoisted declaration using labelsRef
   const drawLabels = useCallback((ctx: CanvasRenderingContext2D, theme: 'dark' | 'light') => {
    if (!showLabels) return;
    const view = viewState.current;
    const canvas = canvasRef.current;
    const currentLabels = labelsRef.current;
    if (!canvas || currentLabels.length === 0) return;

    currentLabels.forEach(l => {
      // Use temporary position if available (during move operations)
      const useX = (l as any).tempX !== undefined ? (l as any).tempX : l.x;
      const useY = (l as any).tempY !== undefined ? (l as any).tempY : l.y;
      
      const pos = worldToCanvas(useX, useY);
      
      // Check if position is valid
      if (!pos || typeof pos.x !== 'number' || typeof pos.y !== 'number') {
        return;
      }
      
      // For fixedSize: maintain constant pixel size regardless of zoom
      const baseScale = l.scale ?? 1;
      let scaleFactor: number;
      if (l.fixedSize) {
        // Fixed size behavior: maintain constant pixel size regardless of zoom
        // Use direct scaling to maintain pixel size like widgets
        scaleFactor = baseScale * view.scale;
      } else {
        // Normal behavior: scale with zoom (smaller when zoom out, larger when zoom in)
        // Use direct scaling like widgets
        scaleFactor = baseScale * view.scale;
      }
      const fontSize = 11 * scaleFactor;
      const padding = 6 * scaleFactor;

      ctx.font = `${fontSize}px Arial`;
      const textWidth = Math.max(ctx.measureText(l.label).width, ctx.measureText(l.value).width);
      const iconS = 6 * scaleFactor;
      const iconPad = 2 * scaleFactor;
      const width = textWidth + padding * 2 + iconS + iconPad;
      const headerH = fontSize + padding;
      const valueH = fontSize + padding;
      const totalH = headerH + valueH + 4 * scaleFactor;

      const startX = pos.x - width / 2;
      const startY = pos.y - totalH - 8 * scaleFactor;

      // Draw header background with theme-based colors
      ctx.fillStyle = theme === 'dark' ? '#3b82f6' : '#1d4ed8';
      ctx.fillRect(startX, startY, width, headerH);

      // Draw value background with theme-based colors
      ctx.fillStyle = theme === 'dark' ? '#2d2d2d' : '#f3f4f6';
      ctx.fillRect(startX, startY + headerH, width, valueH);

      // Border with theme-based colors
      ctx.strokeStyle = theme === 'dark' ? '#1f1f1f' : '#d1d5db';
      ctx.lineWidth = 1;
      ctx.strokeRect(startX, startY, width, headerH + valueH);

      // Store hit area for interactions (includes arrow area)
      const arrowH = 8 * scaleFactor;
      (l as any).hitArea = { x: startX, y: startY, width, height: headerH + valueH + arrowH };
      
      // Draw downward arrow below the label box with theme-based colors
      const boxBottomY = startY + headerH + valueH;
      const arrowW = 12 * scaleFactor;
      ctx.fillStyle = theme === 'dark' ? '#2d2d2d' : '#f3f4f6';
      ctx.beginPath();
      ctx.moveTo(pos.x - arrowW / 2, boxBottomY);
      ctx.lineTo(pos.x + arrowW / 2, boxBottomY);
      ctx.lineTo(pos.x, boxBottomY + arrowH);
      ctx.closePath();
      ctx.fill();
      // optional border for arrow with theme-based colors
      ctx.strokeStyle = theme === 'dark' ? '#1f1f1f' : '#d1d5db';
      ctx.stroke();
      
      // Text with theme-based colors
      ctx.fillStyle = theme === 'dark' ? '#ffffff' : '#082A43';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(l.label, pos.x, startY + headerH / 2);
      ctx.fillText(l.value, pos.x, startY + headerH + valueH / 2);

      const isResizingThis = resizingItem && resizingItem.type === 'label' && resizingItem.id === l.id;
      if (!isResizingThis) {
        // Draw three-dot menu icon with theme-based colors
        const dX = startX + width - iconS - 1;
        const dY = startY + 2 * scaleFactor;
        ctx.fillStyle = theme === 'dark' ? '#ffffff' : '#082A43';
        const rr = iconS / 5;
        for (let i = 0; i < 3; i++) {
          ctx.beginPath();
          ctx.arc(dX + iconS / 2, dY + rr + i * rr * 2.2, rr, 0, 2 * Math.PI);
          ctx.fill();
        }
        (l as any).dotArea = { x: dX, y: dY, width: iconS + 4, height: iconS + 4 };
      } else {
        // Draw resize handle as a rotated caret (arrow) icon, top-right, pointing to top-right (45deg)
        const resizeS = 14 * scaleFactor;
        const resizeX = startX + 6 * scaleFactor + width - resizeS;
        const resizeY = startY - 6 * scaleFactor;
        ctx.save();
        ctx.translate(resizeX + resizeS / 2, resizeY + resizeS / 2);
        ctx.rotate(-Math.PI / 1.3); // 45deg counterclockwise
        ctx.beginPath();
        ctx.moveTo(-resizeS / 3, 0);
        ctx.lineTo(0, resizeS / 3);
        ctx.lineTo(resizeS / 3, 0);
        ctx.lineWidth = 2 * scaleFactor;
        ctx.strokeStyle = theme === 'dark' ? '#fff' : '#111';
        ctx.stroke();
        ctx.restore();
        (l as any).resizeArea = { x: resizeX, y: resizeY, width: resizeS, height: resizeS };
      }
    });
  }, [labels, worldToCanvas, resizingItem, showLabels, theme]);

  // Modify the draw function to check visibility
  const draw = useCallback(() => {
    if (isDrawing.current) return;

    const canvas = canvasRef.current;
    const buffer = bufferRef.current.canvas;
    if (!canvas || !buffer || !isBufferInitialized) return;

    isDrawing.current = true;

    try {
      // Cancel any pending animation frame
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = undefined;
      }

      const view = viewState.current;
      const now = performance.now();

      // Performance optimization: Skip frames if we're rendering too frequently during interaction
      // But allow immediate redraw when grid state changes
      const timeSinceLastDraw = now - lastDrawTime.current;
      if (view.isDragging && timeSinceLastDraw < 16 && !forceRedraw) { // Limit to ~60fps during dragging
        isDrawing.current = false;
        animationFrameRef.current = requestAnimationFrame(() => draw());
        return;
      }
      
      // Reset force redraw flag after using it
      if (forceRedraw) {
        setForceRedraw(false);
      }

      // Set canvas dimensions only if they've changed
      // const dpr = 1; // Force DPR to 1 for better performance
      const rect = canvas.getBoundingClientRect();

      // Only resize canvas if dimensions have changed
      if (canvas.width !== rect.width || canvas.height !== rect.height) {
        canvas.width = rect.width;
        canvas.height = rect.height;
        buffer.width = canvas.width;
        buffer.height = canvas.height;
      }

      // Get contexts
      const ctx = canvas.getContext("2d", { alpha: false });
      const bufferCtx = buffer.getContext("2d", { alpha: false });
      if (!ctx || !bufferCtx) {
        isDrawing.current = false;
        return;
      }

      // THEME: set background
      bufferCtx.fillStyle = theme === 'dark' ? "#1B1B1B" : "#cacaca";
      bufferCtx.fillRect(0, 0, buffer.width, buffer.height);

      let drawnCount = 0;

      // Clear transform cache on each draw
      transformCache.current.clear();

      // Use a cached transform function to avoid recalculating for each point
      const transform = (point: Point) => {
        const key = `${point.x},${point.y}`;
        if (transformCache.current.has(key)) {
          return transformCache.current.get(key)!;
        }
        const result = {
          x: ((point.x - view.bounds.minX) * view.baseScale * view.scale) + view.offsetX,
          y: buffer.height - (((point.y - view.bounds.minY) * view.baseScale * view.scale) + view.offsetY)
        };
        transformCache.current.set(key, result);
        return result;
      };

      // Calculate visible bounds to only render what's visible
      const visibleWorldBounds = {
        minX: (0 - view.offsetX) / (view.baseScale * view.scale) + view.bounds.minX,
        minY: (0 - view.offsetY) / (view.baseScale * view.scale) + view.bounds.minY,
        maxX: (canvas.width - view.offsetX) / (view.baseScale * view.scale) + view.bounds.minX,
        maxY: (canvas.height - view.offsetY) / (view.baseScale * view.scale) + view.bounds.minY
      };

      // Add padding to visible bounds
      const padding = 100 / (view.baseScale * view.scale); // 100px in world coordinates
      visibleWorldBounds.minX -= padding;
      visibleWorldBounds.minY -= padding;
      visibleWorldBounds.maxX += padding;
      visibleWorldBounds.maxY += padding;

      // Function to check if an entity is visible
      const isEntityVisible = (entity: Entity): boolean => {
        if (entity.startPoint && entity.endPoint) {
          // Line entity
          return (
            (entity.startPoint.x >= visibleWorldBounds.minX || entity.endPoint.x >= visibleWorldBounds.minX) &&
            (entity.startPoint.x <= visibleWorldBounds.maxX || entity.endPoint.x <= visibleWorldBounds.maxX) &&
            (entity.startPoint.y >= visibleWorldBounds.minY || entity.endPoint.y >= visibleWorldBounds.minY) &&
            (entity.startPoint.y <= visibleWorldBounds.maxY || entity.endPoint.y <= visibleWorldBounds.maxY)
          );
        } else if (entity.center && entity.radius) {
          // Circle entity
          return (
            entity.center.x + entity.radius >= visibleWorldBounds.minX &&
            entity.center.x - entity.radius <= visibleWorldBounds.maxX &&
            entity.center.y + entity.radius >= visibleWorldBounds.minY &&
            entity.center.y - entity.radius <= visibleWorldBounds.maxY
          );
        } else if (entity.vertices && entity.vertices.length > 0) {
          // Polyline entity - check if any vertex is visible
          return entity.vertices.some(v =>
            v.x >= visibleWorldBounds.minX &&
            v.x <= visibleWorldBounds.maxX &&
            v.y >= visibleWorldBounds.minY &&
            v.y <= visibleWorldBounds.maxY
          );
        } else if (entity.insertionPoint) {
          // Text or block entity
          return (
            entity.insertionPoint.x >= visibleWorldBounds.minX &&
            entity.insertionPoint.x <= visibleWorldBounds.maxX &&
            entity.insertionPoint.y >= visibleWorldBounds.minY &&
            entity.insertionPoint.y <= visibleWorldBounds.maxY
          );
        }

        // Default to visible if we can't determine
        return true;
      };

      // Determine if we need to use simplified rendering based on entity count and interaction state
      const useSimplifiedRendering = view.isDragging;

      // Adaptive sampling rate based on entity count
      let sampleRate = 1;
      if (useSimplifiedRendering) {
        if (view.entities.length > 10000) sampleRate = 10;
        else if (view.entities.length > 5000) sampleRate = 5;
        else if (view.entities.length > 2000) sampleRate = 2;
      }

      // Filter visible entities and apply sampling
      const visibleEntities = view.entities
        .filter((_, i) => i % sampleRate === 0)
        .filter(isEntityVisible);
      // Draw all entities to buffer
      for (let i = 0; i < visibleEntities.length; i++) {
        const entity = visibleEntities[i];
        try {
          const color = getColor(entity, theme);
          bufferCtx.strokeStyle = color;
          bufferCtx.fillStyle = color;

          // Adjust line width based on entity type and layer
          if (!useSimplifiedRendering) {
            if (entity.layer?.toUpperCase().includes('THICK') ||
              entity.layer?.toUpperCase().includes('BOLD')) {
              bufferCtx.lineWidth = 2.0;
            } else if (entity.layer?.toUpperCase().includes('THIN')) {
              bufferCtx.lineWidth = 0.8;
            } else {
              bufferCtx.lineWidth = 1.2;
            }
          } else {
            // Use a constant thin line width during dragging for better performance
            bufferCtx.lineWidth = 1.0;
          }

          switch (entity.type) {
            case "LINE": {
              if (!entity.startPoint || !entity.endPoint) continue;
              const start = transform(entity.startPoint);
              const end = transform(entity.endPoint);
              bufferCtx.beginPath();
              bufferCtx.moveTo(start.x, start.y);
              bufferCtx.lineTo(end.x, end.y);
              bufferCtx.stroke();
              drawnCount++;
              break;
            }
            case "TEXT":
            case "MTEXT": {
              // Always render text to prevent flickering during dragging
              // Update text color for theme
              const originalStrokeStyle = bufferCtx.strokeStyle;
              const originalFillStyle = bufferCtx.fillStyle;
              bufferCtx.strokeStyle = theme === 'dark' ? "#FFFFFF" : "#082A43"; // Theme-based outline for text
              bufferCtx.fillStyle = theme === 'dark' ? "#FFFFFF" : "#082A43"; // Theme-based text
              drawText(bufferCtx, entity, view, theme);
              bufferCtx.strokeStyle = originalStrokeStyle;
              bufferCtx.fillStyle = originalFillStyle;
              drawnCount++;
              break;
            }
            case "CIRCLE": {
              if (!entity.center || !entity.radius) continue;
              const center = transform(entity.center);
              const radius = entity.radius * view.baseScale * view.scale;
              bufferCtx.beginPath();
              bufferCtx.arc(center.x, center.y, radius, 0, 2 * Math.PI);
              bufferCtx.stroke();
              drawnCount++;
              break;
            }
            case "ARC": {
              if (!entity.center || !entity.radius) continue;
              const center = transform(entity.center);
              const radius = entity.radius * view.baseScale * view.scale;
              const startAngle = entity.startAngle ?? 0;
              const endAngle = entity.endAngle ?? 2 * Math.PI;
              bufferCtx.beginPath();
              bufferCtx.arc(
                center.x,
                center.y,
                radius,
                -startAngle,
                -endAngle,
                true
              );
              bufferCtx.stroke();
              drawnCount++;
              break;
            }
            case "LWPOLYLINE": {
              if (!entity.vertices || entity.vertices.length < 2) continue;
              bufferCtx.beginPath();
              const start = transform(entity.vertices[0]);
              bufferCtx.moveTo(start.x, start.y);

              // Skip bulge handling during simplified rendering
              if (useSimplifiedRendering) {
                for (let i = 1; i < entity.vertices.length; i++) {
                  const point = transform(entity.vertices[i]);
                  bufferCtx.lineTo(point.x, point.y);
                }
              } else {
                for (let i = 0; i < entity.vertices.length; i++) {
                  const current = entity.vertices[i];
                  const nextIndex = (i + 1) % entity.vertices.length;

                  // If this is the last point and the polyline is not closed, break
                  if (nextIndex === 0 && !entity.closed) break;

                  const next = entity.vertices[nextIndex];
                  const currentPoint = transform(current);
                  const nextPoint = transform(next);

                  // Check if this segment has a bulge (curved segment)
                  if (current.bulge && current.bulge !== 0) {
                    const bulge = current.bulge;

                    // Calculate the chord (straight line distance between points)
                    const chord = Math.sqrt(
                      Math.pow(nextPoint.x - currentPoint.x, 2) +
                      Math.pow(nextPoint.y - currentPoint.y, 2)
                    );

                    // Calculate arc parameters
                    // const s = chord / 2 * bulge;
                    const r = (chord / 2 / bulge) * (1 + Math.pow(bulge, 2));

                    // Calculate center point of the arc
                    const angle = Math.atan2(nextPoint.y - currentPoint.y, nextPoint.x - currentPoint.x);
                    const centerAngle = angle + (bulge > 0 ? Math.PI / 2 : -Math.PI / 2);
                    const centerX = currentPoint.x + r * Math.cos(centerAngle);
                    const centerY = currentPoint.y + r * Math.sin(centerAngle);

                    // Calculate start and end angles
                    const startAngle = Math.atan2(currentPoint.y - centerY, currentPoint.x - centerX);
                    const endAngle = Math.atan2(nextPoint.y - centerY, nextPoint.x - centerX);

                    // Draw the arc
                    bufferCtx.arc(centerX, centerY, Math.abs(r), startAngle, endAngle, bulge < 0);
                  } else {
                    // Draw straight line
                    bufferCtx.lineTo(nextPoint.x, nextPoint.y);
                  }
                }
              }

              if (entity.closed) {
                bufferCtx.closePath();
              }

              bufferCtx.stroke();
              drawnCount++;
              break;
            }
            case "DIMENSION": {
              // Always render dimension text to prevent flickering during dragging
              const { startPoint, endPoint, text, position } = entity;
              if (!startPoint || !endPoint || !text || !position) break;

              const start = transform(startPoint);
              const end = transform(endPoint);
              const label = transform(position);

              // Draw dimension line
              bufferCtx.beginPath();
              bufferCtx.moveTo(start.x, start.y);
              bufferCtx.lineTo(end.x, end.y);
              bufferCtx.stroke();

              // Draw extension lines
              bufferCtx.beginPath();
              bufferCtx.moveTo(start.x, start.y);
              bufferCtx.lineTo(label.x, label.y);
              bufferCtx.moveTo(end.x, end.y);
              bufferCtx.lineTo(label.x, label.y);
              bufferCtx.stroke();

              // Draw text
              bufferCtx.font = `12px Arial`;
              bufferCtx.fillStyle = theme === 'dark' ? "#FFFFFF" : "#082A43";
              bufferCtx.textAlign = "center";
              bufferCtx.textBaseline = "middle";
              bufferCtx.fillText(text, label.x, label.y - 10);
              drawnCount++;
              break;
            }
            case "ATTDEF": {
              // Draw ATTDEF as text similar to TEXT entity
              if (!entity.startPoint || !entity.text) break;
              
              const position = transform(entity.startPoint);
              const fontSize = Math.max(8, (entity.textHeight || 12) * view.baseScale * view.scale);
              
              // Set text properties
              bufferCtx.font = `${fontSize}px Arial`;
              bufferCtx.fillStyle = theme === 'dark' ? "#FFFFFF" : "#082A43";
              bufferCtx.textAlign = "left";
              bufferCtx.textBaseline = "middle";
              
              // Apply rotation if specified
              if (entity.rotation) {
                bufferCtx.save();
                bufferCtx.translate(position.x, position.y);
                bufferCtx.rotate((entity.rotation * Math.PI) / 180);
                bufferCtx.fillText(entity.text, 0, 0);
                bufferCtx.restore();
              } else {
                bufferCtx.fillText(entity.text, position.x, position.y);
              }
              
              drawnCount++;
              break;
            }
            case "HATCH": {
              if (!entity.boundaryPaths || entity.boundaryPaths.length === 0) break;
              
              // Draw each boundary path
              entity.boundaryPaths.forEach((boundaryPath) => {
                if (!boundaryPath.vertices || boundaryPath.vertices.length < 3) return;
                
                bufferCtx.beginPath();
                
                // Start with the first vertex
                const firstVertex = transform(boundaryPath.vertices[0]);
                bufferCtx.moveTo(firstVertex.x, firstVertex.y);
                
                // Draw lines to subsequent vertices
                for (let i = 1; i < boundaryPath.vertices.length; i++) {
                  const vertex = transform(boundaryPath.vertices[i]);
                  bufferCtx.lineTo(vertex.x, vertex.y);
                }
                
                // Close the path if it's marked as closed
                if (boundaryPath.isClosed) {
                  bufferCtx.closePath();
                }
                
                // Fill the hatch area if it's a solid fill
                if (entity.solidFill === 1) {
                  bufferCtx.fillStyle = theme === 'dark' ? "#333333" : "#E5E5E5";
                  bufferCtx.fill();
                }
                
                // Draw the boundary outline
                bufferCtx.stroke();
              });
              
              drawnCount++;
              break;
            }
            // Skip complex entities during simplified rendering
            case "ELLIPSE":
            case "POLYLINE":
            case "SPLINE":
            case "INSERT": {
              if (useSimplifiedRendering) {
                drawnCount++;
                break;
              }

              // Handle specific entity types
              switch (entity.type) {
                case "ELLIPSE": {
                  if (!entity.center || !entity.majorAxis || !entity.axisRatio) break;

                  const center = transform(entity.center);
                  const radiusX = entity.majorAxis * view.baseScale * view.scale;
                  const radiusY = radiusX * entity.axisRatio;
                  const rotation = (entity.rotation || 0) * (Math.PI / 180);

                  bufferCtx.beginPath();
                  bufferCtx.ellipse(center.x, center.y, radiusX, radiusY, rotation, 0, 2 * Math.PI);
                  bufferCtx.stroke();
                  break;
                }
                case "POLYLINE": {
                  if (!entity.vertices || entity.vertices.length < 2) break;

                  bufferCtx.beginPath();
                  const start = transform(entity.vertices[0]);
                  bufferCtx.moveTo(start.x, start.y);

                  for (let i = 1; i < entity.vertices.length; i++) {
                    const point = transform(entity.vertices[i]);
                    bufferCtx.lineTo(point.x, point.y);
                  }

                  if (entity.closed) {
                    bufferCtx.closePath();
                  }

                  bufferCtx.stroke();
                  break;
                }
                case "SPLINE": {
                  if (!entity.controlPoints || entity.controlPoints.length < 2) break;

                  bufferCtx.beginPath();
                  const first = transform(entity.controlPoints[0]);
                  bufferCtx.moveTo(first.x, first.y);

                  for (let i = 1; i < entity.controlPoints.length - 1; i += 2) {
                    const cp1 = transform(entity.controlPoints[i]);
                    const cp2 = transform(entity.controlPoints[i + 1]);
                    bufferCtx.bezierCurveTo(cp1.x, cp1.y, cp2.x, cp2.y, cp2.x, cp2.y);
                  }

                  bufferCtx.stroke();
                  break;
                }
                case "INSERT": {
                  // Skip INSERT entities during panning for better performance
                  drawnCount++;
                  break;
                }
              }

              drawnCount++;
              break;
            }
          }
        } catch (err) {
          // Handle drawing error silently
        }
      }

      // Copy buffer to main canvas
      ctx.drawImage(buffer, 0, 0);

      // Draw grid first (behind everything)
      drawGrid(ctx);

      // Always draw comments, even during simplified rendering
      drawComments(ctx, theme);
      // INSERT LABELS DRAWING
      drawLabels(ctx, theme);
      // Update stats
      setStats(prev => {
        if (!prev) return null;
        return {
          ...prev,
          drawnEntities: drawnCount,
          scale: view.scale,
          offset: { x: view.offsetX, y: view.offsetY }
        };
      });

      isDrawing.current = false;
      lastDrawTime.current = now;
    } catch (error) {
      isDrawing.current = false;
    }
  }, [isBufferInitialized, drawComments, worldToCanvas, forceRedraw, gridStyle, gridEnabled, gridSpacing, drawLabels, theme]);

  // Keep the processEntities function as is
  const processEntities = useCallback((parsedData: {
    entities?: Entity[];
    blocks?: Record<string, { entities: Entity[] }>;
    tables?: any;
  }) => {
    const allEntities: Entity[] = [];
    const blockEntities: Record<string, Entity[]> = {};

    // First, extract all blocks and their entities
    if (parsedData.blocks) {
      Object.entries(parsedData.blocks).forEach(([blockName, block]) => {
        if (block.entities && Array.isArray(block.entities)) {
          blockEntities[blockName] = block.entities;
        }
      });
    }

    // Process INSERT entities to resolve block references
    const processInsertEntity = (entity: Entity, basePoint: Point = { x: 0, y: 0 }, parentScale: number = 1): Entity[] => {
      if (entity.type !== 'INSERT' || !entity.name && !entity.blockName) {
        return [entity];
      }

      const blockName = entity.name || entity.blockName;
      if (!blockName || !blockEntities[blockName]) {
        return [entity];
      }

      const insertPoint = entity.insertionPoint || { x: 0, y: 0 };
      const scaleX = (entity.scaleX || entity.xScale || 1) * parentScale;
      const scaleY = (entity.scaleY || entity.yScale || 1) * parentScale;
      const rotation = (entity.rotation || 0) * (Math.PI / 180);

      const result: Entity[] = [];

      // Process each entity in the block
      blockEntities[blockName].forEach(blockEntity => {
        // Deep clone the entity to avoid modifying the original
        const clonedEntity = JSON.parse(JSON.stringify(blockEntity));

        // Transform coordinates based on insertion point, scale, and rotation
        const transformPoint = (point: Point): Point => {
          if (!point) return point;

          // Apply scaling
          const scaledX = point.x * scaleX;
          const scaledY = point.y * scaleY;

          // Apply rotation
          const cosR = Math.cos(rotation);
          const sinR = Math.sin(rotation);
          const rotatedX = scaledX * cosR - scaledY * sinR;
          const rotatedY = scaledX * sinR + scaledY * cosR;

          // Apply translation
          return {
            x: rotatedX + insertPoint.x + basePoint.x,
            y: rotatedY + insertPoint.y + basePoint.y,
            z: point.z,
            bulge: point.bulge
          };
        };

        // Transform all points in the entity
        ['startPoint', 'endPoint', 'center', 'position', 'insertionPoint'].forEach(key => {
          if (clonedEntity[key]) {
            clonedEntity[key] = transformPoint(clonedEntity[key]);
          }
        });

        // Transform vertices if they exist
        if (clonedEntity.vertices) {
          clonedEntity.vertices = clonedEntity.vertices.map(transformPoint);
        }

        // Transform control points if they exist
        if (clonedEntity.controlPoints) {
          clonedEntity.controlPoints = clonedEntity.controlPoints.map(transformPoint);
        }

        // Scale radius, majorAxis if present
        if (clonedEntity.radius) {
          clonedEntity.radius *= Math.max(scaleX, scaleY);
        }
        if (clonedEntity.majorAxis) {
          clonedEntity.majorAxis *= scaleX;
        }

        // Recursively process nested INSERTs
        if (clonedEntity.type === 'INSERT') {
          result.push(...processInsertEntity(clonedEntity, insertPoint, Math.max(scaleX, scaleY)));
        } else {
          result.push(clonedEntity);
        }
      });

      return result;
    };

    // Process all entities and resolve blocks
    if (parsedData.entities && Array.isArray(parsedData.entities)) {
      parsedData.entities.forEach(entity => {
        if (entity.type === 'INSERT') {
          allEntities.push(...processInsertEntity(entity));
        } else {
          allEntities.push(entity);
        }
      });
    }

    if (allEntities.length === 0) {
      setError("No entities found in the DXF data");
      return;
    }

    // Calculate initial bounds with padding
    const bounds = {
      minX: Infinity,
      minY: Infinity,
      maxX: -Infinity,
      maxY: -Infinity
    };

    allEntities.forEach(entity => {
      if (entity.startPoint) {
        bounds.minX = Math.min(bounds.minX, entity.startPoint.x);
        bounds.minY = Math.min(bounds.minY, entity.startPoint.y);
        bounds.maxX = Math.max(bounds.maxX, entity.startPoint.x);
        bounds.maxY = Math.max(bounds.maxY, entity.startPoint.y);
      }
      if (entity.endPoint) {
        bounds.minX = Math.min(bounds.minX, entity.endPoint.x);
        bounds.minY = Math.min(bounds.minY, entity.endPoint.y);
        bounds.maxX = Math.max(bounds.maxX, entity.endPoint.x);
        bounds.maxY = Math.max(bounds.maxY, entity.endPoint.y);
      }
      if (entity.center) {
        const r = entity.radius || 0;
        bounds.minX = Math.min(bounds.minX, entity.center.x - r);
        bounds.minY = Math.min(bounds.minY, entity.center.y - r);
        bounds.maxX = Math.max(bounds.maxX, entity.center.x + r);
        bounds.maxY = Math.max(bounds.maxY, entity.center.y + r);
      }
      if (entity.vertices) {
        entity.vertices.forEach(v => {
          bounds.minX = Math.min(bounds.minX, v.x);
          bounds.minY = Math.min(bounds.minY, v.y);
          bounds.maxX = Math.max(bounds.maxX, v.x);
          bounds.maxY = Math.max(bounds.maxY, v.y);
        });
      }
      if (entity.insertionPoint) {
        bounds.minX = Math.min(bounds.minX, entity.insertionPoint.x);
        bounds.minY = Math.min(bounds.minY, entity.insertionPoint.y);
        bounds.maxX = Math.max(bounds.maxX, entity.insertionPoint.x);
        bounds.maxY = Math.max(bounds.maxY, entity.insertionPoint.y);
      }
    });

    // Add padding to bounds
    const padding = 50;
    bounds.minX -= padding;
    bounds.minY -= padding;
    bounds.maxX += padding;
    bounds.maxY += padding;

    // Calculate initial scale to fit the canvas perfectly (100% fit)
    const canvas = canvasRef.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();

    // Calculate diagram dimensions
    const diagramWidth = bounds.maxX - bounds.minX || 1;
    const diagramHeight = bounds.maxY - bounds.minY || 1;
    const diagramArea = diagramWidth * diagramHeight;
    const entityCount = allEntities.length;
    
    // Adaptive padding based on entity count and diagram size
    let paddingPercentage = 0.05; // Start with 5% padding
    
    // Reduce padding for larger diagrams or higher entity counts
    if (entityCount > 10000 || diagramArea > 1000000) {
      paddingPercentage = 0.02; // 2% padding for very large diagrams
    } else if (entityCount > 5000 || diagramArea > 500000) {
      paddingPercentage = 0.03; // 3% padding for large diagrams
    } else if (entityCount > 1000 || diagramArea > 100000) {
      paddingPercentage = 0.04; // 4% padding for medium diagrams
    }
    
    const paddingX = rect.width * paddingPercentage;
    const paddingY = rect.height * paddingPercentage;
    
    // Calculate scale to fit exactly within canvas bounds
    const scaleX = (rect.width - paddingX * 2) / diagramWidth;
    const scaleY = (rect.height - paddingY * 2) / diagramHeight;
    const baseScale = Math.min(scaleX, scaleY); // Use the smaller scale to ensure everything fits
    
    // Adaptive initial zoom based on entity count and diagram complexity
    let initialZoom = 1.0; // Default to 100% fit
    
    // For smaller diagrams with fewer entities, we can zoom in a bit more
    if (entityCount < 500 && diagramArea < 50000) {
      initialZoom = 1.2; // 20% zoom in for small diagrams
    } else if (entityCount < 1000 && diagramArea < 100000) {
      initialZoom = 1.1; // 10% zoom in for medium-small diagrams
    }
    // For larger diagrams, keep it at 1.0 (100% fit) or even zoom out slightly
    else if (entityCount > 10000 || diagramArea > 1000000) {
      initialZoom = 0.95; // Zoom out 5% for very large diagrams
    }
    
    // Always apply the calculated scaling
    viewState.current.entities = allEntities;
    viewState.current.bounds = bounds;
      viewState.current.baseScale = baseScale;
    viewState.current.scale = initialZoom;
    setCurrentZoom(initialZoom);
    
    // Center the drawing with the calculated zoom
    const scaledWidth = diagramWidth * baseScale * initialZoom;
    const scaledHeight = diagramHeight * baseScale * initialZoom;
    viewState.current.offsetX = (rect.width - scaledWidth) / 2;
    viewState.current.offsetY = (rect.height - scaledHeight) / 2;

    // Initial draw
    draw();

    setStats({
      totalEntities: allEntities.length,
      drawnEntities: allEntities.length,
      bounds,
      scale: viewState.current.scale
    });
  }, [draw]);


  // Handle mouse down for panning - React event handler
  const handleReactMouseDown = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
    // Don't start panning if we're in move mode
    if (movingItem) return;
    
    // Only handle left click for panning
    if (e.button !== 0) return;

    const view = viewState.current;
    view.isDragging = true;
    view.lastX = e.clientX;
    view.lastY = e.clientY;
  }, [movingItem]);

  // Handle mouse down for panning - DOM event handler
  const handleDOMMouseDown = useCallback((e: MouseEvent) => {
    // Don't start panning if we're in move mode
    if (movingItem) return;
    
    // If we are in resize mode, handle specially
    if (resizingItem) {
      const canvas = canvasRef.current;
      if (canvas) {
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Get resize area of current item
        let resizeRect: { x: number; y: number; width: number; height: number } | undefined;
        if (resizingItem.type === 'comment') {
          const c = comments.find(c => c.id === resizingItem.id);
          resizeRect = (c as any)?.resizeArea;
        } else {
          const l = labels.find(l => l.id === resizingItem.id);
          resizeRect = (l as any)?.resizeArea;
        }
        // Click inside handle starts dragging resize
        if (resizeRect && pointInRect(x, y, resizeRect)) {
          setResizingItem(prev => prev ? { ...prev, startX: e.clientX } : prev);
          // prevent panning
          e.stopPropagation();
          e.preventDefault();
          return;
        } else {
          // Click outside handle cancels resize mode
          setResizingItem(null);
          // Do not call draw here; let state update trigger redraw
        }
      }
    }

    // Only handle left click for panning
    if (e.button !== 0) return;

    const view = viewState.current;
    view.isDragging = true;
    view.lastX = e.clientX;
    view.lastY = e.clientY;
  }, [comments, labels, resizingItem, movingItem, draw]);

  // Handle comment submission
  // Handle document submission from modal
  const handleDocumentSubmit = useCallback(async (name: string, file: File) => {
    if (!newDocument) return;

    if (documentModalMode === 'upload' && file) {
      // Upload new document
      setIsUploadingDocument(true);
      try {
        const formData = new FormData();
        
        // If user provided a custom name, create a new file with that name
        const fileToUpload = name && name.trim() ? new File([file], name.trim() + file.name.substring(file.name.lastIndexOf('.')), { type: file.type }) : file;
        
        formData.append('file', fileToUpload);
        
        const response = await uploadFile('asset_doc', formData);
        if (response.status === 200) {
          // Refresh available documents list
          const docsResponse = await getAssetDocs();
          if (docsResponse.status === 200) {
            // Transform API response to match expected structure
            const transformedDocuments = docsResponse.data.map((doc: any) => ({
              id: doc.fileUniqueId,
              name: doc.fileName,
              fileId: doc.fileId,
              fileName: doc.fileName,
              ext: doc.ext,
              size: doc.size,
              createdOnUTC: doc.createdOnUTC
            }));
            setAvailableDocuments(transformedDocuments);
          }
          // Switch back to select mode and clear form
          setDocumentModalMode('select');
          setNewDocument((prev: any) => prev ? {...prev, name: '', file: null} : null);
          setSelectedDocumentId(''); // Clear selection so user can choose the new document
        }
      } catch (error) {
        console.error('Failed to upload document:', error);
      } finally {
        setIsUploadingDocument(false);
      }
      return;
    }

    // Handle adding document to canvas (either from selection or after successful upload)
    setDocuments(prev => {
      if (newDocument.id) {
        // Update existing document
        return prev.map(doc =>
          doc.id === newDocument.id
            ? { 
                ...doc, 
                name, 
                file: file || doc.file // Keep existing file if no new file provided
              }
            : doc
        );
      } else {
        // Add new document
        const newDocumentObj: Document = {
          id: Date.now().toString(),
          x: newDocument.x,
          y: newDocument.y,
          name,
          file,
          fileId: (file as any)?.fileId, // Store fileId if available
          createdAt: new Date().toISOString()
        };
        return [...prev, newDocumentObj];
      }
    });

    // Close modal and reset state after adding document to canvas
    setNewDocument(null);
    setAddDocumentOpen(false);
    setSelectedDocumentId('');
  }, [newDocument, documentModalMode]);

  const handleCommentSubmit = useCallback(() => {
    if (!newComment || !commentText.trim()) return;

    setComments(prev => {
      let updatedComments;

      if (isEditingComment && newComment.id) {
        // Update existing comment
        updatedComments = prev.map(comment =>
          comment.id === newComment.id
            ? { ...comment, text: commentText.trim() }
            : comment
        );
      } else {
        // Add new comment
        const newCommentObj: Comment = {
          id: Date.now().toString(),
          x: newComment.x,
          y: newComment.y,
          text: commentText.trim(),
          createdAt: new Date().toISOString()
        };
        updatedComments = [...prev, newCommentObj];
      }

      // Force immediate redraw before state updates
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          draw();
        }
      }

      return updatedComments;
    });

    setNewComment(null);
    setCommentText("");
    setIsEditingComment(false);
  }, [newComment, commentText, isEditingComment, draw]);

  // Handle comment cancellation
  const handleCommentCancel = useCallback(() => {
    setNewComment(null);
    setCommentText("");
    setIsEditingComment(false);
  }, []);

  // Handle document cancellation
  const handleDocumentCancel = useCallback(() => {
    setNewDocument(null);
  }, []);


  // Update mouse handlers to use RAF throttling
  const handleMouseMove = useCallback((e: MouseEvent) => {
    const canvas = canvasRef.current;
    const view = viewState.current;
    if (!canvas) return;

    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // --- MOVE MODE --------------------------------------------------
    if (movingItem) {
      // Check if view state is properly initialized before allowing move operations
      const view = viewState.current;
      if (!view.bounds || typeof view.bounds.minX !== 'number' || typeof view.bounds.minY !== 'number') {
        return;
      }
      
      canvas.style.cursor = 'move';
      const world = canvasToWorld(x, y);
      
      // Check if world coordinates are valid
      if (!world || typeof world.x !== 'number' || typeof world.y !== 'number') {
        return;
      }
      
      // Apply grid snapping if enabled (restore old grid behavior)
      const snappedWorld = snapToGrid(world.x, world.y);
      
      // Store position in ref to avoid excessive state updates
      movePositionRef.current = snappedWorld;

      // Update the item position in the ref for visual feedback without triggering state updates
      if (movingItem.type === 'document') {
        // Handle document movement first to ensure smooth document dragging
        const doc = documents.find(d => d.id === movingItem.id);
        if (doc) {
          (doc as any).tempX = snappedWorld.x;
          (doc as any).tempY = snappedWorld.y;
        }
      } else if (movingItem.type === 'comment') {
        const comment = comments.find(c => c.id === movingItem.id);
        if (comment) {
          (comment as any).tempX = snappedWorld.x;
          (comment as any).tempY = snappedWorld.y;
        }
      } else if (movingItem.type === 'label') {
        const label = labels.find(l => l.id === movingItem.id);
        if (label) {
          (label as any).tempX = snappedWorld.x;
          (label as any).tempY = snappedWorld.y;
        }
      } else if (movingItem.type === 'widget') {
        const widget = widgets.find(w => w.id === movingItem.id);
        if (widget) {
          (widget as any).tempX = snappedWorld.x;
          (widget as any).tempY = snappedWorld.y;
        }
      }

      if (!animationFrameRef.current) {
        animationFrameRef.current = requestAnimationFrame(() => {
          draw();
          animationFrameRef.current = undefined;
        });
      }
      return; // Skip panning
    }

    // --- RESIZE MODE --------------------------------------------------
    if (resizingItem && !Number.isNaN(resizingItem.startX)) {
      const deltaXResize = e.clientX - resizingItem.startX;
      // change scale based on horizontal drag, use sensitivity factor
      const sensitivity = 300; // pixels corresponding to scale 1 change
      let newScale = resizingItem.initialScale + deltaXResize / sensitivity;
      // Set minimum and maximum scale limits depending on type
      if (resizingItem.type === 'document') {
        newScale = Math.max(0.3, Math.min(3, newScale)); // More restricted range for documents
      } else {
        newScale = Math.max(0.01, newScale); // No maximum limit for other types
      }

      if (resizingItem.type === 'comment') {
        setComments(prev => prev.map(c => c.id === resizingItem.id ? { ...c, scale: newScale } : c));
      } else if (resizingItem.type === 'label') {
        setLabels(prev => {
          const updated = prev.map(l => l.id === resizingItem.id ? { ...l, scale: newScale } : l);
          labelsRef.current = updated;
          return updated;
        });
      } else if (resizingItem.type === 'widget') {
        // Widget resizing will be handled by the WidgetLayer component
        // We'll need to communicate with the useDxfWidgets context
      }

      if (!animationFrameRef.current) {
        animationFrameRef.current = requestAnimationFrame(() => {
          draw();
          animationFrameRef.current = undefined;
        });
      }
      // do not proceed to panning
      return;
    }

    // --- PAN MODE ---------------------------------------------------
    if (!view.isDragging) return;

    // Calculate delta movement
    const deltaX = e.clientX - view.lastX;
    const deltaY = e.clientY - view.lastY;

    // Only update if movement is significant enough
    if (Math.abs(deltaX) < 0.5 && Math.abs(deltaY) < 0.5) return;

    view.offsetX += deltaX;
    view.offsetY -= deltaY;
    view.lastX = e.clientX;
    view.lastY = e.clientY;

    // request redraw
    if (!animationFrameRef.current) {
      animationFrameRef.current = requestAnimationFrame(() => {
        draw();
        animationFrameRef.current = undefined;
      });
    }
  }, [draw, movingItem, canvasToWorld, resizingItem]);

  const handleMouseUp = useCallback(() => {
    // If we were resizing, finalize and clear state
    if (resizingItem && !Number.isNaN(resizingItem.startX)) {
      setResizingItem(null);
      // Do not call draw here; let state update trigger redraw
      return;
    }

    // If we were moving an item, finalize the position
    if (movingItem && movePositionRef.current) {
      // Finalize the position - grid snapping already applied during movement
      const finalPos = movePositionRef.current;
      
      if (movingItem.type === 'document') {
        // Handle document movement first for smooth interaction
        setDocuments(prev => prev.map(d => d.id === movingItem.id ? { ...d, x: finalPos.x, y: finalPos.y } : d));
        const doc = documents.find(d => d.id === movingItem.id);
        if (doc) {
          delete (doc as any).tempX;
          delete (doc as any).tempY;
        }
      } else if (movingItem.type === 'comment') {
        setComments(prev => prev.map(c => c.id === movingItem.id ? { ...c, x: finalPos.x, y: finalPos.y } : c));
      } else if (movingItem.type === 'label') {
        setLabels(prev => {
          const updated = prev.map(l => l.id === movingItem.id ? { ...l, x: finalPos.x, y: finalPos.y } : l);
          labelsRef.current = updated;
          return updated;
        });
      } else if (movingItem.type === 'widget') {
        // Finalize widget movement and clear temporary position
        updateWidgetPosition(movingItem.id, finalPos.x, finalPos.y);
        const widget = widgets.find(w => w.id === movingItem.id);
        if (widget) {
          delete (widget as any).tempX;
          delete (widget as any).tempY;
        }
      }
      
      // Clear temporary position and moving state
      movePositionRef.current = null;
      setMovingItem(null);
      return;
    }

    const view = viewState.current;
    if (view.isDragging) {
      view.isDragging = false;
      // Final render at full quality after dragging stops
      requestAnimationFrame(() => draw());
    }
  }, [draw, resizingItem, movingItem]);

  // Modify the handleWheel function for better zoom control
  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    const view = viewState.current;
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Get mouse position relative to canvas
    const rect = canvas.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;

    // Calculate zoom factor with smoother scaling
    const zoomSpeed = 0.1; // Increased zoom speed for more noticeable changes
    const delta = e.deltaY > 0 ? -zoomSpeed : zoomSpeed;
    const zoomFactor = Math.exp(delta);

    // Calculate new scale with minimum limit only (no maximum zoom limit)
    const newScale = Math.max(0.02, view.scale * zoomFactor); // Only minimum scale limit, unlimited zoom in

    // Only update if the scale actually changed
    if (newScale !== view.scale) {
      // Get canvas dimensions for coordinate conversion
      const canvas = canvasRef.current;
      const canvasHeight = canvas?.height || 0;
      
      // Store the current world point under the mouse
      // For Y-axis, we need to account for the inverted coordinate system
      const worldX = (mouseX - view.offsetX) / view.scale;
      const worldY = ((canvasHeight - mouseY) - view.offsetY) / view.scale;
      
      // Update the scale first
      view.scale = newScale;
      
      // Calculate new offset to keep the same world point under the mouse
      // This ensures the zoom focuses exactly on the mouse cursor
      view.offsetX = mouseX - worldX * newScale;
      view.offsetY = (canvasHeight - mouseY) - worldY * newScale;
      
      // Update the current zoom in the widgets context
      setCurrentZoom(newScale);

      // Use requestAnimationFrame for better performance
      if (!animationFrameRef.current) {
        animationFrameRef.current = requestAnimationFrame(() => {
          draw();
          animationFrameRef.current = undefined;
        });
      }
    }
  }, [draw]);

  const handleUpload = async () => {
    const file = fileRef.current?.files?.[0];
    if (!file) {
      setError("Please select a file before rendering");
      return;
    }

    // Clear previous comments in state (do not persist blank yet)
    setComments([]);
    setError(null);
    setLoading(true);

    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    const formData = new FormData();
    formData.append("file", file);

    // Decide endpoint based on extension
    let endpoint = '';
    if (fileExtension === 'dwg') {
      endpoint = 'https://dxf-dwg-parser-api.processhub.ai/convert-dwg';
    } else if (fileExtension === 'dxf') {
      endpoint = 'https://dxf-dwg-parser-api.processhub.ai/upload';
    } else {
      setLoading(false);
      return setError("Unsupported file type. Please upload a .dwg or .dxf file.");
    }

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData || "Upload Failed");
      }

      const parsedData = await response.json();
        // Get unique types
      processEntities(parsedData);
      // Persist parsed data and empty comments to backend (single call)
      if (fileRef.current) fileRef.current.value = "";
      setSelectedFileName("");
      updateTabData(activeTabId, { data: JSON.stringify(parsedData), comments: [] });

    } catch (error: any) {
      setError(error.message || "Failed to upload and parse DXF file.");
    } finally {
      setLoading(false);
    }
  };

  const [showClearModal, setShowClearModal] = useState(false);

  useEffect(() => {
    // Add event listeners
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.addEventListener('mousedown', handleDOMMouseDown);
    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mousemove', handleCanvasMouseMove);
    canvas.addEventListener('click', handleCanvasClick);
    window.addEventListener('mouseup', handleMouseUp);
    canvas.addEventListener('wheel', handleWheel, { passive: false });

    // Cleanup
    return () => {
      canvas.removeEventListener('mousedown', handleDOMMouseDown);
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mousemove', handleCanvasMouseMove);
      canvas.removeEventListener('click', handleCanvasClick);
      window.removeEventListener('mouseup', handleMouseUp);
      canvas.removeEventListener('wheel', handleWheel);
    };
  }, [handleDOMMouseDown, handleMouseMove, handleMouseUp, handleWheel, handleCanvasClick, handleCanvasMouseMove]);

  // Focus comment input when it appears and auto-resize for existing content
  useEffect(() => {
    if (newComment && commentInputRef.current) {
      commentInputRef.current.focus();
      
      // Auto-resize the textarea to fit existing content
      const textarea = commentInputRef.current;
      textarea.style.height = 'auto';
      textarea.style.height = Math.min(textarea.scrollHeight, 200) + 'px';
    }
  }, [newComment, commentText]);

  // Add a final optimization to improve rendering performance
  useEffect(() => {
    // Set up a render loop for smooth animations
    let lastFrameTime = 0;
    const targetFPS = 60;
    const frameInterval = 1000 / targetFPS;

    const renderLoop = (timestamp: number) => {
      // Skip frames to maintain target FPS
      if (timestamp - lastFrameTime < frameInterval) {
        animationFrameRef.current = requestAnimationFrame(renderLoop);
        return;
      }

      lastFrameTime = timestamp;

      // Only redraw if we're dragging or if there's a pending draw
      const view = viewState.current;
      if (view.isDragging) {
        draw();
      }

      animationFrameRef.current = requestAnimationFrame(renderLoop);
    };

    // Start the render loop
    animationFrameRef.current = requestAnimationFrame(renderLoop);

    // Clean up
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [draw]);

  // Fetch available documents when document modal opens
  useEffect(() => {
    if (newDocument && !newDocument.id && !documentsFetchedRef.current) { // Only fetch when adding new document, not editing, and not already fetched
      const fetchDocuments = async () => {
        try {
          const response = await getAssetDocs();
          if (response.status === 200) {
            // Transform API response to match expected structure
            const transformedDocuments = response.data.map((doc: any) => ({
              id: doc.fileUniqueId, // Use fileUniqueId as unique identifier
              name: doc.fileName, // Use fileName for display
              fileId: doc.fileId,
              fileName: doc.fileName,
              ext: doc.ext,
              size: doc.size,
              createdOnUTC: doc.createdOnUTC
            }));
            setAvailableDocuments(transformedDocuments);
            setDocumentModalMode('select'); // Start with select mode if documents exist
            documentsFetchedRef.current = true; // Mark as fetched for this modal session
          }
        } catch (error) {
          // Handle error silently
          setAvailableDocuments([]);
          setDocumentModalMode('upload'); // Fall back to upload mode if fetch fails
          documentsFetchedRef.current = true; // Mark as fetched even on error
        }
      };
      fetchDocuments();
    } else if (!newDocument) {
      // Reset when modal closes
      documentsFetchedRef.current = false;
    }
  }, [newDocument]); // Depend on newDocument to trigger fetch when modal opens

  // Handle file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setSelectedFileName(file ? file.name : "No file selected");
    setError(null);
  };

  useEffect(() => {
    const resizeCanvas = () => {
      const canvas = canvasRef.current;
      const buffer = bufferRef.current.canvas;
      if (!canvas || !buffer) return;

      // Get the parent container's size
      const parent = canvas.parentElement;
      if (!parent) return;
      const rect = parent.getBoundingClientRect();

      // Set canvas width/height attributes (not just style)
      canvas.width = rect.width;
      canvas.height = rect.height;
      buffer.width = rect.width;
      buffer.height = rect.height;

      // Redraw after resizing
      draw();
    };

    // Initial resize
    resizeCanvas();

    // Listen for window resize
    window.addEventListener('resize', resizeCanvas);

    // Optionally, use ResizeObserver for more robust container resizing
    const parent = canvasRef.current?.parentElement;
    let observer: ResizeObserver | undefined;
    if (parent && typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(resizeCanvas);
      observer.observe(parent);
    }

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (observer && parent) observer.unobserve(parent);
    };
  }, [draw, bufferRef, canvasRef]);



  useEffect(() => { 
    labelsRef.current = labels; 
  }, [labels]);

 

  const handleLabelSubmit = useCallback(async () => {
    if (!newLabel || !labelInput.trim()) return;
    let formatted = labelValue;
    let tagId: number | undefined;
    let expressionId: number | undefined;
    if (selectedSource === 'tag') {
      tagId = selectedTagId;
      try {
        const r: any = await apiFunctionsRef.current.getTagData([tagId]);
        if (r.status === 200 && r.data?.length) formatted = r.data[0].formattedValue;
      } catch (e) { /* ignore fetch error */ }
    } else {
      expressionId = selectedExpressionId;
      try {
        const r: any = await apiFunctionsRef.current.getExpressionData([expressionId]);
        if (r.status === 200 && r.data?.length) formatted = r.data[0].formattedValue;
      } catch (e) { /* ignore fetch error */ }
    }
    setLabels(prev => {
      let updatedLabels;
      if (isEditingLabel && newLabel.id) {
        updatedLabels = prev.map(l => {
          if (l.id !== newLabel.id) return l;

          // Build updated object ensuring correct source + IDs
          const updated: Label = {
            ...l,
            label: labelInput.trim(),
            value: formatted,
            source: selectedSource,
            tagId: selectedSource === 'tag' ? tagId : undefined,
            expressionId: selectedSource === 'expression' ? expressionId : undefined,
          };
          return updated;
        });
      } else {
        const newObj: Label = { id: Date.now().toString(), x: newLabel.x, y: newLabel.y, label: labelInput.trim(), source: selectedSource, value: formatted, createdAt: new Date().toISOString(), tagId, expressionId };
        updatedLabels = [...prev, newObj];
      }
      
      // Update the ref immediately to ensure polling uses the latest labels
      labelsRef.current = updatedLabels;
      return updatedLabels;
    });
    draw();
    setNewLabel(null);
    setLabelInput("");
    setIsEditingLabel(false);
  }, [newLabel, labelInput, labelValue, isEditingLabel, selectedSource, selectedTagId, selectedExpressionId, draw]);

  // INSERT HANDLE LABEL CANCEL
  const handleLabelCancel = useCallback(() => {
    setNewLabel(null);
    setLabelInput("");
    setIsEditingLabel(false);
  }, []);


  // Add label delete handler near others
  // const handleLabelDelete = useCallback(() => {
  //   if (!newLabel?.id) return;
  //   setLabels(prev => prev.filter(l => l.id !== newLabel.id));
  //   setNewLabel(null);
  //   setLabelInput("");
  //   setIsEditingLabel(false);
  // }, [newLabel]);

  // when opening add modal via context menu (two occurrences) replace setNewLabel lines to set source/tag default
  // first occurrence placeholder removed

  // second occurrence similar change

  // when clicking label for edit adjust selections
  // (handled inside handleCanvasClick)

  // Modal UI: placeholder block removed
  /* <div className="mb-2 grid gap-2">
    <input
      type="text"
      value={labelInput}
      onChange={(e) => setLabelInput(e.target.value)}
      placeholder="Label"
      className="w-full px-2 py-1.5 bg-[#082A43] border border-gray-700 rounded-md text-white placeholder-gray-500 text-sm focus:outline-none focus:border-blue-500"
    />
    <select
      value={selectedSource}
      onChange={e=>setSelectedSource(e.target.value as any)}
      className="w-full px-2 py-1.5 bg-[#082A43] border border-gray-700 rounded-md text-white text-sm focus:outline-none focus:border-blue-500"
    >
      <option value="tag">Tags</option>
      <option value="expression">Expressions</option>
    </select>
    {selectedSource==='tag' ? (
      <select value={selectedTagId} onChange={e=>setSelectedTagId(Number(e.target.value))} className="w-full px-2 py-1.5 bg-[#082A43] border border-gray-700 rounded-md text-white text-sm focus:outline-none focus:border-blue-500">
        {tagsList.map(t=> (<option key={t.id} value={t.id}>{t.tagNameIdentifier}</option>))}
      </select>
    ) : (
      <select value={selectedExpressionId} onChange={e=>setSelectedExpressionId(Number(e.target.value))} className="w-full px-2 py-1.5 bg-[#082A43] border border-gray-700 rounded-md text-white text-sm focus:outline-none focus:border-blue-500">
        {exprList.map(ex=> (<option key={ex.id} value={ex.id}>{ex.tagNameIdentifier}</option>))}
      </select>
    )}
  </div>; */

  // Placeholder JSX removed

  // Poll tag & expression values every 15 s and update labels
  useEffect(() => {
    // Only start polling if we have labels and we're on a DXF viewer tab
    if (!currentTab || currentTab.type !== 'dxf-viewer') {
      return;
    }

    // Don't start polling if there are no labels for this tab
    if (!labels || labels.length === 0) {
      return;
    }

    // Add a flag to prevent multiple intervals
    let isActive = true;

    const fetchAndUpdate = async () => {
      // Check if component is still active
      if (!isActive) return;
      
      try {
        // Use the current labels from ref to ensure we're polling the latest labels
        const currentLabels = labelsRef.current;
        
        // Don't poll if no labels exist
        if (!currentLabels || currentLabels.length === 0) {
          return;
        }

        const tagIds = currentLabels.filter(l => l.source === 'tag' && l.tagId).map(l => l.tagId as number);
        const exprIds = currentLabels.filter(l => l.source === 'expression' && l.expressionId).map(l => l.expressionId as number);

        // Don't make API calls if no IDs to fetch
        if (tagIds.length === 0 && exprIds.length === 0) {
          return;
        }

        const tagValueMap: Record<number, string> = {};
        if (tagIds.length) {
          const res: any = await apiFunctionsRef.current.getTagData(tagIds);
          if (res.status === 200) {
            res.data.forEach((t: any) => { tagValueMap[t.id] = t.formattedValue; });
          }
        }

        const exprValueMap: Record<number, string> = {};
        if (exprIds.length) {
          const res2: any = await apiFunctionsRef.current.getExpressionData(exprIds);
          if (res2.status === 200) {
            res2.data.forEach((e: any) => { exprValueMap[e.id] = e.formattedValue; });
          }
        }

        const updated = currentLabels.map(l => {
          if (l.source === 'tag' && l.tagId !== undefined) {
            return tagValueMap[l.tagId] !== undefined ? { ...l, value: tagValueMap[l.tagId] } : l;
          }
          if (l.source === 'expression' && l.expressionId !== undefined) {
            return exprValueMap[l.expressionId] !== undefined ? { ...l, value: exprValueMap[l.expressionId] } : l;
          }
          return l;
        });

        labelsRef.current = updated;
        setLabels(updated);
        draw();
      } catch (err) {
        // Handle polling error silently
      }
    };

    // Start polling since we already checked that labels exist
    
    // Don't run initial fetch immediately - wait for the interval
    // This prevents the polling from overwriting newly added labels
    const interval = setInterval(fetchAndUpdate, 15000);
    
    return () => {
      isActive = false;
      clearInterval(interval);
    };
  }, [currentTab?.id, draw]);

  useEffect(() => {
    if (!itemActionMenu) return;
    const handleOutside = (e: MouseEvent) => {
      if (itemActionRef.current && !itemActionRef.current.contains(e.target as Node)) {
        setItemActionMenu(null);
      }
    };
    window.addEventListener('mousedown', handleOutside);
    return () => window.removeEventListener('mousedown', handleOutside);
  }, [itemActionMenu]);

  // Add click-outside handler for comment modal
  useEffect(() => {
    if (!newComment) return;
    const handleOutside = (e: MouseEvent) => {
      if (commentModalRef.current && !commentModalRef.current.contains(e.target as Node)) {
        handleCommentCancel();
      }
    };
    window.addEventListener('mousedown', handleOutside);
    return () => window.removeEventListener('mousedown', handleOutside);
  }, [newComment]);

  // Add click-outside handler for label modal
  useEffect(() => {
    if (!newLabel) return;
    const handleOutside = (e: MouseEvent) => {
      if (labelModalRef.current && !labelModalRef.current.contains(e.target as Node)) {
        handleLabelCancel();
      }
    };
    window.addEventListener('mousedown', handleOutside);
    return () => window.removeEventListener('mousedown', handleOutside);
  }, [newLabel]);

  // Add click-outside handler for document modal
  useEffect(() => {
    if (!newDocument) return;
    const handleOutside = (e: MouseEvent) => {
      if (documentModalRef.current && !documentModalRef.current.contains(e.target as Node)) {
        handleDocumentCancel();
      }
    };
    window.addEventListener('mousedown', handleOutside);
    return () => window.removeEventListener('mousedown', handleOutside);
  }, [newDocument]);

  // Add click-outside handler for grid settings menu
  useEffect(() => {
    if (!showGridSettings) return;
    const handleOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      const gridSettingsButton = document.querySelector('[title="Grid Settings"]');
      const gridSettingsMenu = document.querySelector('.grid-settings-menu');
      
      if (gridSettingsButton && !gridSettingsButton.contains(target) && 
          gridSettingsMenu && !gridSettingsMenu.contains(target)) {
        setShowGridSettings(false);
      }
    };
    window.addEventListener('mousedown', handleOutside);
    return () => window.removeEventListener('mousedown', handleOutside);
  }, [showGridSettings]);

  // After definitions add effect to update cursor when movingItem changes
  // Set cursor to 'move' while in move mode
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (resizingItem) {
      canvas.style.cursor = 'ne-resize';
    } else if (movingItem) {
      canvas.style.cursor = 'move';
    } else {
      canvas.style.cursor = viewState.current.isDragging ? 'grabbing' : 'grab';
    }
  }, [movingItem, resizingItem]);

  // confirmation dialog for deleting comment / label / widget via action menu
  const [pendingDelete, setPendingDelete] = useState<{ type: 'comment' | 'label' | 'widget' | 'document'; id: string } | null>(null);

  // --- ADD helper to check if point inside rect ---
  const pointInRect = (px: number, py: number, rect: { x: number; y: number; width: number; height: number }) => px >= rect.x && px <= rect.x + rect.width && py >= rect.y && py <= rect.y + rect.height;

  useEffect(() => {
    draw();
  }, [gridStyle, gridEnabled, gridSpacing, draw]);

  const clearDataConfirmed = useCallback(() => {
    if (!currentTab) return;
    setComments([]);
    setLabels([]);
    labelsRef.current = [];
    setDocuments([]); // Clear all documents from the canvas
    documentsLoadedRef.current = false; // Reset loaded flag
    lastSavedDocumentsRef.current = ''; // Reset saved reference
    viewState.current.entities = [];
    clearAllWidgets(); // Clear all widgets from the canvas
    
    // Reset stats to null to clear the information panel
    setStats(null);
    
    // Hide the action bar since there's no data
    setShowActionBar(false);
    
    // Reset view state to initial values
    viewState.current.scale = 1;
    viewState.current.offsetX = 0;
    viewState.current.offsetY = 0;
    viewState.current.baseScale = 1;
    viewState.current.bounds = {
      minX: 0,
      minY: 0,
      maxX: 0,
      maxY: 0
    };
    
    draw();
    if (fileRef.current) fileRef.current.value = "";
    setSelectedFileName("");
    updateTabData(activeTabId, { data: '', comments: [], labels: [], widgets: [], documents: [] });
    setShowClearModal(false);
  }, [currentTab, activeTabId, updateTabData, draw, clearAllWidgets]);

  const handleClearData = useCallback(() => {
    setShowClearModal(true);
  }, []);

  // Add handler to recenter viewport
  const handleReturnToCenter = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const view = viewState.current;
    // Reset scale and offsets using the same adaptive logic as initial load
    if (view.bounds && view.entities) {
      const diagramWidth = view.bounds.maxX - view.bounds.minX || 1;
      const diagramHeight = view.bounds.maxY - view.bounds.minY || 1;
      const diagramArea = diagramWidth * diagramHeight;
      const entityCount = view.entities.length;
      
      // Adaptive padding based on entity count and diagram size
      let paddingPercentage = 0.05; // Start with 5% padding
      
      if (entityCount > 10000 || diagramArea > 1000000) {
        paddingPercentage = 0.02; // 2% padding for very large diagrams
      } else if (entityCount > 5000 || diagramArea > 500000) {
        paddingPercentage = 0.03; // 3% padding for large diagrams
      } else if (entityCount > 1000 || diagramArea > 100000) {
        paddingPercentage = 0.04; // 4% padding for medium diagrams
      }
      
      const paddingX = rect.width * paddingPercentage;
      const paddingY = rect.height * paddingPercentage;
      
      // Calculate scale to fit exactly within canvas bounds
      const scaleX = (rect.width - paddingX * 2) / diagramWidth;
      const scaleY = (rect.height - paddingY * 2) / diagramHeight;
      const baseScale = Math.min(scaleX, scaleY);
      
      // Adaptive initial zoom based on entity count and diagram complexity
      let initialZoom = 1.0; // Default to 100% fit
      
      if (entityCount < 500 && diagramArea < 50000) {
        initialZoom = 1.2; // 20% zoom in for small diagrams
      } else if (entityCount < 1000 && diagramArea < 100000) {
        initialZoom = 1.1; // 10% zoom in for medium-small diagrams
      } else if (entityCount > 10000 || diagramArea > 1000000) {
        initialZoom = 0.95; // Zoom out 5% for very large diagrams
      }
      
      view.baseScale = baseScale;
      view.scale = initialZoom;
      setCurrentZoom(initialZoom);
      
      // Center the drawing with the calculated zoom
      const scaledWidth = diagramWidth * baseScale * initialZoom;
      const scaledHeight = diagramHeight * baseScale * initialZoom;
      view.offsetX = (rect.width - scaledWidth) / 2;
      view.offsetY = (rect.height - scaledHeight) / 2;
      draw();
    }
  };

  // Handle action bar hide with animation
  const handleHideActionBar = () => {
    setActionBarAnimating(true);
    setTimeout(() => {
      setShowActionBar(false);
      setActionBarAnimating(false);
    }, 300); // Match the animation duration
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  type AddWidgetFunction = (widget: any) => void;
  
  const AddWidgetBridge = ({ onReady }: { onReady: (addWidget: AddWidgetFunction) => void }) => {
    const { addWidget } = useDxfWidgets();
    useEffect(() => { 
      onReady(addWidget); 
    }, [onReady, addWidget]); // Add proper deps
    return null;
  };

  const { updateWidgetScale, updateWidgetFixedSize, removeWidget } = useDxfWidgets();
  const WidgetActionBridge: React.FC = () => {
    
    // Widget move operations are now handled directly in handleMouseUp

    // Listen for widget resize operations from DxfCanvasViewer
    useEffect(() => {
      if (resizingItem && resizingItem.type === 'widget' && !Number.isNaN(resizingItem.startX)) {
        // Calculate new scale based on resize operation
        const deltaX = resizingItem.startX - resizingItem.startX; // This will be updated in handleMouseMove
        const sensitivity = 300;
        let newScale = resizingItem.initialScale + deltaX / sensitivity;
        newScale = Math.max(0.3, Math.min(5, newScale));
        updateWidgetScale(resizingItem.id, newScale);
      }
    }, [resizingItem, updateWidgetScale]);

    // Listen for widget delete operations from DxfCanvasViewer
    // Note: This will be handled in the confirmation modal's Yes button instead
    // to prevent immediate deletion when modal opens
    // useEffect(() => {
    //   if (pendingDelete && pendingDelete.type === 'widget') {
        // removeWidget(pendingDelete.id);
    //   }
    // }, [pendingDelete, removeWidget]);

    return null;
  };

  // Bridge to handle widget context menu from WidgetLayer
  const WidgetContextMenuBridge: React.FC = () => {
    const { setWidgetContextMenu } = useDxfWidgets();
    
    // Connect the setWidgetContextMenu function to handleWidgetContextMenu
    useEffect(() => {
      // Override the setWidgetContextMenu function to use our handler
      // Store the handler in the ref
      (setWidgetContextMenu as any).__handler = widgetContextMenuHandlerRef.current;
    }, [setWidgetContextMenu]);

    return null;
  };

  // Function to handle widget context menu from WidgetLayer
  // Type for the widget context menu handler
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  type WidgetContextMenuHandler = (widgetId: string, screenX: number, screenY: number) => void;
  
  const handleWidgetContextMenu = useCallback((widgetId: string, screenX: number, screenY: number) => {
    setItemActionMenu({ type: 'widget', id: widgetId, screenX, screenY });
  }, []);

  // Ref to store the widget context menu handler
  const widgetContextMenuHandlerRef = useRef<WidgetContextMenuHandler | null>(null);
  widgetContextMenuHandlerRef.current = handleWidgetContextMenu;

  const WorldToCanvasBridge: React.FC = () => {
    const { setWorldToCanvas, setCurrentZoom } = useDxfWidgets();
    useEffect(() => {
      setWorldToCanvas(worldToCanvas);
    }, [setWorldToCanvas]);
    
    // Update current zoom whenever view scale changes
    useEffect(() => {
      setCurrentZoom(viewState.current.scale);
    }, [setCurrentZoom]);
    
    return null;
  };

  return (
    <>
      <AddWidgetBridge onReady={(fn)=>{
        addWidgetFnRef.current = fn;
      }} />
      <WidgetActionBridge />
      <WidgetContextMenuBridge />
      <WorldToCanvasBridge />

      {(forceConfig) && (
        <div className="p-3 bg-[#082A43] border-b border-gray-700 flex items-center justify-between z-10 relative">
          <div className="flex items-center gap-3">
            {!hasDxfData && (
              <>
                <div className="relative flex items-center">
                  <input
                    ref={fileRef}
                    type="file"
                    name="file"
                    accept=".dxf,.dwg"
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                    onChange={handleFileChange}
                  />
                  <button className="text-sm px-3 py-1.5 bg-[#1B1B1B] text-gray-300 rounded-md border border-gray-700 hover:bg-[#2B2B2B] hover:text-white transition-colors">
                    Choose File
                  </button>
                  <span className="ml-2 text-sm text-gray-400 truncate max-w-[200px]">
                    {selectedFileName || "No file selected"}
                  </span>
                </div>
                {fileChosen && (
                  <button
                    onClick={handleUpload}
                    disabled={loading}
                    className={cn(
                      "btn px-4 py-1.5 text-sm",
                      loading ? "opacity-50 cursor-not-allowed" : ""
                    )}
                  >
                    {loading ? (
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 border-2 border-gray-400 border-t-white rounded-full animate-spin"></div>
                        <span>Processing...</span>
                      </div>
                    ) : (
                      <span>Render File</span>
                    )}
                  </button>
                )}
              </>
            )}
            {hasDxfData && (
              <button
                onClick={handleClearData}
                className="btn-danger px-4 py-1.5 text-sm"
              >
                Clear Data
              </button>
            )}
          </div>
        </div>
      )}
      <div className="h-screen relative bg-[#1B1B1B]">
        {error ? (
          <div className="p-4 text-red-500 bg-red-900/20 rounded m-4">{error}</div>
        ) : (
          <>
            <div className="relative w-full h-full">
            <canvas
              ref={canvasRef}
              onMouseDown={handleReactMouseDown}
              className="absolute inset-0 z-0"
              style={{
                width: '100%',
                height: '100%',
                background: "#1B1B1B",
                cursor: viewState.current.isDragging ? 'grabbing' : 'grab'
              }}
            />
              {/* Position WidgetLayer relative to canvas for proper coordinate mapping */}
                <div className="absolute inset-0 pointer-events-none z-[9]">
                  <WidgetLayer isWidgetResizing={isWidgetResizing} widgetResizingId={widgetResizingId} setIsWidgetResizing={setIsWidgetResizing} setWidgetResizingId={setWidgetResizingId} movingItem={movingItem} showWidgets={showWidgets} colorTheme={colorTheme} />
                </div>
                {/* Document Layer */}
                <div className="absolute inset-0 pointer-events-none">
                  <DocumentLayer
                    isDocumentResizing={isDocumentResizing}
                    documentResizingId={documentResizingId}
                    setIsDocumentResizing={setIsDocumentResizing}
                    setDocumentResizingId={setDocumentResizingId}
                    movingItem={movingItem}
                    showDocuments={showDocuments}
                    documents={documents}
                    onDocumentMove={(id, x, y) => {
                      // Set the modifying flag during movement
                      isModifyingDocumentRef.current = true;
                      setDocuments(prev => prev.map(d => d.id === id ? { ...d, x, y } : d));
                      // Clear the flag after a short delay
                      setTimeout(() => {
                        isModifyingDocumentRef.current = false;
                      }, 100);
                    }}
                    onDocumentScale={(id, scale) => {
                      // Set the modifying flag to prevent saving during scale operations
                      isModifyingDocumentRef.current = true;
                      setDocuments(prev => prev.map(d => d.id === id ? { ...d, scale } : d));
                      // Clear the flag after the current event loop
                      setTimeout(() => {
                        isModifyingDocumentRef.current = false;
                      }, 100);
                    }}
                    onDocumentDelete={(id) => {
                      setDocuments(prev => prev.filter(d => d.id !== id));
                    }}
                    onDocumentContextMenu={(id, x, y) => {
                      setItemActionMenu({ type: 'document', id, screenX: x, screenY: y });
                    }}
                    getWorldToCanvas={worldToCanvas}
                    currentZoom={viewState.current.scale}
                    colorTheme={colorTheme}
                    theme={theme}
                  />
                </div>
            </div>
            
            
            <AddWidgetModal 
              open={addWidgetOpen} 
              onOpenChange={(open) => {
                setAddWidgetOpen(open);
                if (!open) {
                  setEditingWidget(null);
                }
              }} 
              editingWidget={editingWidget}
              onSave={(w)=>{
                if (editingWidget) {
                  // Update existing widget
                  updateWidget(editingWidget.id, w.type, w.config);
                  setEditingWidget(null);
                } else {
                  // Add new widget - show persistent toast message
                  setPendingWidget(w);
                  showToast("📍 Click on the schema where you want to place the widget or press ESC to cancel.", 0, true);
                }
              }} 
            />
            {/* Top Center Action Bar - Only show when there's DXF data */}
            {hasDxfData && (
              <div className="absolute left-1/2 top-2 z-50" style={{ transform: 'translateX(-50%)' }}>
              {!showActionBar ? (
                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
              <button
                        onClick={() => setShowActionBar(true)}
                        className={`bg-black/70 text-white rounded-full shadow-lg p-1.5 transition-all duration-200 ${theme === 'light' ? 'bg-white text-black border border-gray-300' : ''}`}
                        style={{ width: 32, height: 32, minWidth: 32, minHeight: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
                          border: theme === 'light' ? '1px solid #000' : 'none'
                         }}
                      >
                        <FiChevronDown size={18}
                          color={theme === 'light' ? 'black' : 'white'}
                        />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" sideOffset={5} style={{ zIndex: 70 }} variant="dark">
                      Show Actions
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : (
                <div className={`flex flex-col items-center ${actionBarAnimating ? 'animate-fade-out' : 'animate-fade-in'}`}>
                  {/* Fixed position button that doesn't move */}
                  <div className="flex items-center justify-center mb-2">
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={handleHideActionBar}
                            className={`bg-black/70 text-white rounded-full shadow-lg p-1.5 transition-all duration-200 ${theme === 'light' ? 'bg-white text-black border border-gray-300' : ''}`}
                            style={{ width: 32, height: 32, minWidth: 32, minHeight: 32, display: 'flex', alignItems: 'center', justifyContent: 'center',
                              border: theme === 'light' ? '1px solid #000' : 'none'
                             }}
                          >
                            <FiChevronUp size={18}
                              color={theme === 'light' ? 'black' : 'white'}
                            />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" sideOffset={5} style={{ zIndex: 70 }} variant="dark">
                          Hide Actions
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                </div>
                  <div
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-xl shadow-xl ${theme === 'light' ? 'bg-white text-black border border-gray-300' : 'bg-black/80 text-white border border-gray-700'} backdrop-blur transition-all duration-300 ease-in-out`}
                    style={{ minHeight: 36 }}
                  >
                    {/* THEME TOGGLE BUTTON */}
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button
                            onClick={() => {setTheme(theme === 'dark' ? 'light' : 'dark');
                              persistThemeMode(theme === 'dark' ? 'light' : 'dark');
                            }}
                            className="p-1 rounded-full transition-colors border border-transparent hover:border-cyan-400"
                            style={{ color: theme === 'dark' ? '#fbbf24' : '#0284c7', background: theme === 'light' ? '#f3f4f6' : 'transparent' }}
                          >
                            {theme === 'dark' ? <FiMoon size={18} /> : <FiSun size={18} />}
              </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" sideOffset={5} style={{ zIndex: 70 }} variant="dark">
                          {theme === 'dark' ? 'Switch to Light Theme' : 'Switch to Dark Theme'}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    
                    {/* Return to Center */}
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                    <button
                            onClick={handleReturnToCenter}
                            className="p-1 rounded-full transition-colors"
                            style={{ color: theme === 'light' ? '#111' : 'white' }}
                    >
                            <FiTarget size={18} />
                    </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" sideOffset={5} style={{ zIndex: 70 }} variant="dark">
                          Return to Center
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    
                    
                    {/* Add Widget */}
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button onClick={() => {
                            setAddWidgetOpen(true);
                          }} className="p-1 rounded-full transition-colors" style={{ color: theme === 'light' ? '#111' : 'white' }}>
                            <FiPlus size={18} />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" sideOffset={5} style={{ zIndex: 70 }} variant="dark">Add Widget</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>

                    {/* Add Comment */}
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button 
                            onClick={() => {
                              setClickMode('comment');
                              showToast('💬 Click on the schema where you want to add the comment or press ESC to cancel.', 0, true);
                            }} 
                            className="p-1 rounded-full transition-colors"
                            style={{ color: clickMode === 'comment' ? '#7ED34E' : (theme === 'light' ? '#111' : 'white') }}
                          >
                            <MdOutlineComment size={18} />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" sideOffset={5} style={{ zIndex: 70 }} variant="dark">
                          {clickMode === 'comment' ? 'Cancel Comment Mode' : 'Add Comment'}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>

                    {/* Add Sensor */}
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button 
                            onClick={() => {
                              setClickMode('sensor');
                              showToast('🏷️ Click on the schema where you want to add the sensor label or press ESC to cancel.', 0, true);
                            }} 
                            className="p-1 rounded-full transition-colors"
                            style={{ color: clickMode === 'sensor' ? '#7ED34E' : (theme === 'light' ? '#111' : 'white') }}
                          >
                            <FiTag size={18} />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" sideOffset={5} style={{ zIndex: 70 }} variant="dark">
                          {clickMode === 'sensor' ? 'Cancel Sensor Mode' : 'Add Sensor Label'}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>

                    
                    {/* Add Document */}
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <button 
                            onClick={() => {
                              setClickMode('document');
                              setAddDocumentOpen(false); // Ensure the full modal is closed
                              showToast('📄 Click on the schema where you want to add the document or press ESC to cancel.', 0, true);
                            }} 
                            className="p-1 rounded-full transition-colors"
                            style={{ color: clickMode === 'document' ? '#7ED34E' : (theme === 'light' ? '#111' : 'white') }}
                          >
                            <FiFile size={18} />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" sideOffset={5} style={{ zIndex: 70 }} variant="dark">
                          {clickMode === 'document' ? 'Cancel Document Mode' : 'Add Document'}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>

                    
                    {/* Toggle Comments - Only show if comments exist */}
                    {comments.length > 0 && (
                      <TooltipProvider delayDuration={0}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => setShowComments(v => !v)}
                              className="p-1 rounded-full transition-colors"
                              style={{ color: showComments ? '#fff' : (theme === 'light' ? '#111' : '#7ED34E') }}
                            >
                              <MdOutlineCommentsDisabled size={18} 
                                color={theme === 'light' ? 'black' : 'white'}
                              />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="bottom" sideOffset={5} style={{ zIndex: 70 }} variant="dark">
                            {showComments ? 'Hide Comments' : 'Show Comments'}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                    
                    {/* Toggle Labels - Only show if labels exist */}
                    {(labels.length > 0 || widgets.length > 0) && (
                      <TooltipProvider delayDuration={0}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => {
                                setShowLabels(v => !v);
                                setShowWidgets(v => !v);
                              }}
                              className="p-1 rounded-full transition-colors"
                              style={{ color: showLabels ? '#fff' : (theme === 'light' ? '#111' : '#7ED34E') }}
                            >
                              <TbTagOff size={18}
                                color={theme === 'light' ? 'black' : 'white'} />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="bottom" sideOffset={5} style={{ zIndex: 70 }} variant="dark">
                            {showLabels ? 'Hide Sensor Labels & Widgets' : 'Show Sensor Labels & Widgets'}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}



                    {/* Toggle Documents - Only show if documents exist */}
                    {documents.length > 0 && (
                      <TooltipProvider delayDuration={0}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => setShowDocuments(v => !v)}
                              className="p-1 rounded-full transition-colors"
                              style={{ color: showDocuments ? '#fff' : (theme === 'light' ? '#111' : '#7ED34E') }}
                            >
                              <FiFileMinus size={18} 
                                color={theme === 'light' ? 'black' : 'white'}/>
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side="bottom" sideOffset={5} style={{ zIndex: 70 }} variant="dark">
                            {showDocuments ? 'Hide Documents' : 'Show Documents'}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}

                    {/* Grid Toggle */}
                    <TooltipProvider delayDuration={0}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                      <button
                        onClick={() => {
                              setGridEnabled(v => !v);
                              setForceRedraw(true);
                              requestAnimationFrame(() => draw());
                            }}
                            className="p-1 rounded-full transition-colors"
                            style={{ color: gridEnabled ? '#7ED34E' : (theme === 'light' ? '#111' : 'white') }}
                          >
                            <MdOutlineGrid4X4 size={18} />
                      </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" sideOffset={5} style={{ zIndex: 70 }} variant="dark">
                          {gridEnabled ? 'Hide Grid' : 'Show Grid'}
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    
                    {/* If grid is enabled, show grid settings and style toggles */}
                    {gridEnabled && (
                      <>
                        {/* Grid Settings (spacing) */}
                        <TooltipProvider delayDuration={0}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                onClick={() => setShowGridSettings(v => !v)}
                                className="p-1 rounded-full transition-colors"
                                style={{ color: showGridSettings ? '#7ED34E' : (theme === 'light' ? '#111' : 'white') }}
                              >
                                <TbTableExport size={18} />
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" sideOffset={5} style={{ zIndex: 70 }} variant="dark">
                              Configure Grid
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        
                        {/* Grid Style Toggle */}
                        <TooltipProvider delayDuration={0}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                onClick={() => setGridStyle(s => s === 'solid' ? 'dotted' : 'solid')}
                                className="p-1 rounded-full transition-colors"
                                style={{ color: '#7ED34E' }}
                              >
                                {gridStyle === 'solid' ? <FaBorderAll size={18} /> : <FaBorderNone size={18} />}
                              </button>
                            </TooltipTrigger>
                            <TooltipContent side="bottom" sideOffset={5} style={{ zIndex: 70 }} variant="dark">
                              {gridStyle === 'solid' ? 'Solid Grid' : 'Dotted Grid'}
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </>
                    )}
                  </div>
                  {/* Grid Settings Popover */}
                  {showGridSettings && gridEnabled && (
                    <div 
                      className="mt-2 px-4 py-3 rounded-xl shadow-xl bg-black/90 border border-gray-700 flex flex-col items-center animate-fade-in"
                      style={{ 
                        zIndex: -1, 
                        position: 'relative'
                      }}
                    >
                      <label className="text-xs text-gray-400 mb-1 font-medium">Grid Spacing</label>
                        <input
                          type="range"
                        min="10"
                        max="1000"
                          value={gridSpacing}
                        onChange={e => {
                            setGridSpacing(Number(e.target.value));
                          setForceRedraw(true);
                          requestAnimationFrame(() => draw());
                        }}
                        className="w-40 h-2 rounded-lg appearance-none cursor-pointer bg-gray-700"
                      />
                      <span className="text-sm font-mono font-medium min-w-[2rem] text-center text-cyan-400 mt-1">{gridSpacing}</span>
                      </div>
                  )}
                </div>
              )}
            </div>
            )}
            {newComment && (
              <div
                ref={commentModalRef}
                className="absolute bg-[#082A43] p-3 rounded-md shadow-xl "
                style={{
                  left: `${newComment.screenX ? newComment.screenX : worldToCanvas(newComment.x, newComment.y).x + 10}px`,
                  top: `${newComment.screenY ? newComment.screenY : worldToCanvas(newComment.x, newComment.y).y + 10}px`,
                  zIndex: 100, // Above widgets but below other UI elements
                  pointerEvents: 'auto',
                  minWidth: '250px',
                  animation: 'fadeIn 0.2s ease-out'
                }}
              >
                {/* Comment Header */}
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-gray-300 text-sm">
                    {isEditingComment ? 'Edit Comment' : 'New Comment'}
                  </h3>
                  <div className="flex items-center gap-2">
                    {/* Delete moved to action menu */}
                    <button
                      onClick={handleCommentCancel}
                      className="text-gray-500 hover:text-gray-300 transition-colors"
                      aria-label="Close"
                    >
                      ✕
                    </button>
                  </div>
                </div>

                {/* Comment Input */}
                <div className="mb-2">
                  <textarea
                    ref={commentInputRef}
                    value={commentText}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Limit to 500 characters
                      if (value.length <= 500) {
                        setCommentText(value);
                        // Auto-resize
                        e.target.style.height = 'auto';
                        e.target.style.height = Math.min(e.target.scrollHeight, 200) + 'px';
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleCommentSubmit();
                      }
                      if (e.key === 'Escape') handleCommentCancel();
                    }}
                    className="w-full px-2 py-1.5 bg-[#082A43] border border-[#4d4d4d] rounded-md text-white placeholder-gray-500 text-sm focus:outline-none focus:border-blue-500 transition-colors resize-none overflow-hidden"
                    placeholder={isEditingComment ? "Edit comment..." : "Type your comment... (max 500 characters)"}
                    rows={1}
                    maxLength={500}
                  />
                  <div className="text-xs text-gray-500 mt-1 text-right">
                    {commentText.length}/500
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={handleCommentCancel}
                    className="flex-1 px-3 py-1.5 bg-blue-600/20 text-blue-400 hover:bg-blue-500 hover:text-white rounded-md text-sm transition-all duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCommentSubmit}
                    className="px-3 py-1.5 bg-gray text-gray-400 hover:text-white rounded-md text-sm transition-colors"
                  >
                    {isEditingComment ? 'Update' : 'Add'}
                  </button>
                </div>
              </div>
            )}
            {newLabel && (
              <div
                ref={labelModalRef}
                className="absolute bg-[#082A43] p-3 rounded-md shadow-xl "
                style={{
                  left: `${newLabel.screenX ?? worldToCanvas(newLabel.x, newLabel.y).x + 10}px`,
                  top: `${newLabel.screenY ?? worldToCanvas(newLabel.x, newLabel.y).y + 10}px`,
                  zIndex: 100, // Above widgets but below other UI elements
                  pointerEvents: 'auto',
                  minWidth: '250px',
                  animation: 'fadeIn 0.2s ease-out'
                }}
              >
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-gray-300 text-sm">{isEditingLabel ? 'Edit Sensor' : 'Add Sensor'}</h3>
                  <div className="flex items-center gap-2">
                    {/* Delete moved to action menu */}
                    <button
                      onClick={handleLabelCancel}
                      className="text-gray-500 hover:text-gray-300 transition-colors"
                      aria-label="Close"
                    >
                      ✕
                    </button>
                  </div>
                </div>
                <div className="mb-2 grid gap-2">
                  <input
                    type="text"
                    value={labelInput}
                    onChange={(e) => setLabelInput(e.target.value)}
                    placeholder="Label"
                    className="w-full px-2 py-1.5 bg-[#082A43] border border-[#4d4d4d] rounded-md text-white placeholder-gray-500 text-sm focus:outline-none focus:border-blue-500"
                  />
                  <select
                    value={selectedSource}
                    onChange={e => setSelectedSource(e.target.value as any)}
                    className="w-full px-2 py-1.5 bg-[#082A43] border border-[#4d4d4d] rounded-md text-white text-sm focus:outline-none focus:border-blue-500"
                  >
                    <option value="tag">Tags</option>
                    <option value="expression">Expressions</option>
                  </select>
                  {selectedSource === 'tag' ? (
                    <Select
                      value={tagsList.find(t => t.id === selectedTagId) ? { value: selectedTagId, label: tagsList.find(t => t.id === selectedTagId)?.tagNameIdentifier || '' } : null}
                      onChange={(option) => setSelectedTagId(option ? option.value : 0)}
                      options={tagsList.map(t => ({ value: t.id, label: t.tagNameIdentifier }))}
                      placeholder="Select Tag"
                      isSearchable
                      isClearable
                      className="react-select-container"
                      classNamePrefix="react-select"
                      styles={{
                        control: (provided, state) => ({
                          ...provided,
                          backgroundColor: '#082A43',
                          borderColor: state.isFocused ? '#3b82f6' : '#4d4d4d',
                          borderWidth: '1px',
                          borderRadius: '6px',
                          minHeight: '36px',
                          boxShadow: 'none',
                          '&:hover': {
                            borderColor: '#6b7280'
                          }
                        }),
                        valueContainer: (provided) => ({
                          ...provided,
                          padding: '2px 8px',
                          color: 'white'
                        }),
                        input: (provided) => ({
                          ...provided,
                          color: 'white'
                        }),
                        placeholder: (provided) => ({
                          ...provided,
                          color: '#9ca3af'
                        }),
                        singleValue: (provided) => ({
                          ...provided,
                          color: 'white'
                        }),
                        menu: (provided) => ({
                          ...provided,
                          backgroundColor: '#082A43',
                          border: '1px solid #4d4d4d',
                          borderRadius: '6px',
                          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                        }),
                        menuList: (provided) => ({
                          ...provided,
                          padding: '4px',
                          maxHeight: '200px'
                        }),
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isSelected 
                            ? '#3b82f6' 
                            : state.isFocused 
                              ? '#1B1B1B' 
                              : 'transparent',
                          color: state.isSelected ? 'white' : '#d1d5db',
                          borderRadius: '4px',
                          margin: '2px 0',
                          padding: '8px 12px',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: state.isSelected ? '#3b82f6' : '#1B1B1B',
                            color: 'white'
                          }
                        }),
                        clearIndicator: (provided) => ({
                          ...provided,
                          color: '#9ca3af',
                          '&:hover': {
                            color: 'white'
                          }
                        }),
                        dropdownIndicator: (provided) => ({
                          ...provided,
                          color: '#9ca3af',
                          '&:hover': {
                            color: 'white'
                          }
                        })
                      }}
                    />
                  ) : (
                    <Select
                      value={exprList.find(ex => ex.id === selectedExpressionId) ? { value: selectedExpressionId, label: `${exprList.find(ex => ex.id === selectedExpressionId)?.name} - ${exprList.find(ex => ex.id === selectedExpressionId)?.unitName}` } : null}
                      onChange={(option) => setSelectedExpressionId(option ? option.value : 0)}
                      options={exprList.map(ex => ({ value: ex.id, label: `${ex.name} - ${ex.unitName}` }))}
                      placeholder="Select Expression"
                      isSearchable
                      isClearable
                      className="react-select-container"
                      classNamePrefix="react-select"
                      styles={{
                        control: (provided, state) => ({
                          ...provided,
                          backgroundColor: '#082A43',
                          borderColor: state.isFocused ? '#3b82f6' : '#4d4d4d',
                          borderWidth: '1px',
                          borderRadius: '6px',
                          minHeight: '36px',
                          boxShadow: 'none',
                          '&:hover': {
                            borderColor: '#6b7280'
                          }
                        }),
                        valueContainer: (provided) => ({
                          ...provided,
                          padding: '2px 8px',
                          color: 'white'
                        }),
                        input: (provided) => ({
                          ...provided,
                          color: 'white'
                        }),
                        placeholder: (provided) => ({
                          ...provided,
                          color: '#9ca3af'
                        }),
                        singleValue: (provided) => ({
                          ...provided,
                          color: 'white'
                        }),
                        menu: (provided) => ({
                          ...provided,
                          backgroundColor: '#082A43',
                          border: '1px solid #4d4d4d',
                          borderRadius: '6px',
                          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                        }),
                        menuList: (provided) => ({
                          ...provided,
                          padding: '4px',
                          maxHeight: '200px'
                        }),
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isSelected 
                            ? '#3b82f6' 
                            : state.isFocused 
                              ? '#1B1B1B' 
                              : 'transparent',
                          color: state.isSelected ? 'white' : '#d1d5db',
                          borderRadius: '4px',
                          margin: '2px 0',
                          padding: '8px 12px',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: state.isSelected ? '#3b82f6' : '#1B1B1B',
                            color: 'white'
                          }
                        }),
                        clearIndicator: (provided) => ({
                          ...provided,
                          color: '#9ca3af',
                          '&:hover': {
                            color: 'white'
                          }
                        }),
                        dropdownIndicator: (provided) => ({
                          ...provided,
                          color: '#9ca3af',
                          '&:hover': {
                            color: 'white'
                          }
                        })
                      }}
                    />
                  )}
                </div>
                <div className="flex gap-2 mt-2">
                  <button
                    onClick={handleLabelCancel}
                    className="flex-1 px-3 py-1.5 bg-blue-600/20 text-blue-400 hover:bg-blue-500 hover:text-white rounded-md text-sm transition-all duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleLabelSubmit}
                    className="px-3 py-1.5 bg-gray text-gray-400 hover:text-white rounded-md text-sm transition-colors"
                  >
                    {isEditingLabel ? 'Save' : 'Add'}
                  </button>
                </div>
              </div>
            )}
            {newDocument && (
              <div
                ref={documentModalRef}
                className="absolute bg-[#082A43] p-3 rounded-md shadow-xl"
                style={{
                  left: `${newDocument.screenX ?? worldToCanvas(newDocument.x, newDocument.y).x + 10}px`,
                  top: `${newDocument.screenY ?? worldToCanvas(newDocument.x, newDocument.y).y + 10}px`,
                  zIndex: 999,
                  pointerEvents: 'auto',
                  minWidth: '280px',
                  animation: 'fadeIn 0.2s ease-out'
                }}
              >
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-gray-300 text-sm">{newDocument.id ? 'Edit Document' : 'Add Document'}</h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleDocumentCancel}
                      className="text-gray-500 hover:text-gray-300 transition-colors"
                      aria-label="Close"
                    >
                      ✕
                    </button>
                  </div>
                </div>

                <div className="mb-2 grid gap-2">
                  {/* Show dropdown and name input when editing */}
                  {newDocument.id && availableDocuments.length > 0 && (
                    <>
                      <input
                        type="text"
                        value={newDocument.name || ''}
                        onChange={(e) => {
                          setNewDocument((prev: any) => prev ? {...prev, name: e.target.value} : null);
                        }}
                        placeholder="Document Name"
                        className="w-full px-2 py-1.5 bg-[#082A43] border border-[#4d4d4d] rounded-md text-white placeholder-gray-500 text-sm focus:outline-none focus:border-blue-500"
                      />
                      <Select
                        value={availableDocuments.find(doc => doc.fileId === (newDocument as any).fileId) ? { value: (newDocument as any).fileId, label: availableDocuments.find(doc => doc.fileId === (newDocument as any).fileId)?.name || '' } : null}
                        onChange={(option) => {
                          if (option) {
                            const selectedDoc = availableDocuments.find(doc => doc.fileId === option.value);
                            if (selectedDoc) {
                              setNewDocument((prev: any) => prev ? {
                                ...prev,
                                name: selectedDoc.name,
                                fileId: selectedDoc.fileId,
                                fileName: selectedDoc.fileName
                              } : null);
                            }
                          }
                        }}
                        options={availableDocuments.map(doc => ({ value: doc.fileId, label: doc.name }))}
                        placeholder="Select document"
                        isSearchable
                        className="react-select-container"
                        classNamePrefix="react-select"
                        styles={{
                          control: (provided, state) => ({
                            ...provided,
                            backgroundColor: '#082A43',
                            borderColor: state.isFocused ? '#3b82f6' : '#4d4d4d',
                            borderWidth: '1px',
                            borderRadius: '6px',
                            minHeight: '36px',
                            boxShadow: 'none',
                            '&:hover': {
                              borderColor: '#6b7280'
                            }
                          }),
                          valueContainer: (provided) => ({
                            ...provided,
                            padding: '2px 8px',
                            color: 'white'
                          }),
                          input: (provided) => ({
                            ...provided,
                            color: 'white'
                          }),
                          placeholder: (provided) => ({
                            ...provided,
                            color: '#9ca3af'
                          }),
                          singleValue: (provided) => ({
                            ...provided,
                            color: 'white'
                          }),
                          menu: (provided) => ({
                            ...provided,
                            backgroundColor: '#082A43',
                            border: '1px solid #4d4d4d',
                            borderRadius: '6px',
                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                          }),
                          menuList: (provided) => ({
                            ...provided,
                            padding: '4px',
                            maxHeight: '200px'
                          }),
                          option: (provided, state) => ({
                            ...provided,
                            backgroundColor: state.isSelected
                              ? '#3b82f6'
                              : state.isFocused
                                ? '#1B1B1B'
                                : 'transparent',
                            color: state.isSelected ? 'white' : '#d1d5db',
                            borderRadius: '4px',
                            margin: '2px 0',
                            padding: '8px 12px',
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: state.isSelected ? '#3b82f6' : '#1B1B1B',
                              color: 'white'
                            }
                          }),
                          clearIndicator: (provided) => ({
                            ...provided,
                            color: '#9ca3af',
                            '&:hover': {
                              color: 'white'
                            }
                          }),
                          dropdownIndicator: (provided) => ({
                            ...provided,
                            color: '#9ca3af',
                            '&:hover': {
                              color: 'white'
                            }
                          })
                        }}
                      />
                    </>
                  )}

                  {/* Show dropdown if in select mode and documents exist and not editing */}
                  {documentModalMode === 'select' && availableDocuments.length > 0 && !newDocument.id && (
                    <>
                      <Select
                        value={selectedDocumentId ? availableDocuments.find(doc => doc.id === selectedDocumentId) ? { value: selectedDocumentId, label: availableDocuments.find(doc => doc.id === selectedDocumentId)?.name || '' } : null : null}
                        onChange={(option) => setSelectedDocumentId(option ? option.value : '')}
                        options={availableDocuments.map(doc => ({ value: doc.id, label: doc.name }))}
                        placeholder="Select existing document"
                        isSearchable
                        isClearable
                        className="react-select-container"
                        classNamePrefix="react-select"
                        styles={{
                          control: (provided, state) => ({
                            ...provided,
                            backgroundColor: '#082A43',
                            borderColor: state.isFocused ? '#3b82f6' : '#4d4d4d',
                            borderWidth: '1px',
                            borderRadius: '6px',
                            minHeight: '36px',
                            boxShadow: 'none',
                            '&:hover': {
                              borderColor: '#6b7280'
                            }
                          }),
                          valueContainer: (provided) => ({
                            ...provided,
                            padding: '2px 8px',
                            color: 'white'
                          }),
                          input: (provided) => ({
                            ...provided,
                            color: 'white'
                          }),
                          placeholder: (provided) => ({
                            ...provided,
                            color: '#9ca3af'
                          }),
                          singleValue: (provided) => ({
                            ...provided,
                            color: 'white'
                          }),
                          menu: (provided) => ({
                            ...provided,
                            backgroundColor: '#082A43',
                            border: '1px solid #4d4d4d',
                            borderRadius: '6px',
                            boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                          }),
                          menuList: (provided) => ({
                            ...provided,
                            padding: '4px',
                            maxHeight: '200px'
                          }),
                          option: (provided, state) => ({
                            ...provided,
                            backgroundColor: state.isSelected
                              ? '#3b82f6'
                              : state.isFocused
                                ? '#1B1B1B'
                                : 'transparent',
                            color: state.isSelected ? 'white' : '#d1d5db',
                            borderRadius: '4px',
                            margin: '2px 0',
                            padding: '8px 12px',
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: state.isSelected ? '#3b82f6' : '#1B1B1B',
                              color: 'white'
                            }
                          }),
                          clearIndicator: (provided) => ({
                            ...provided,
                            color: '#9ca3af',
                            '&:hover': {
                              color: 'white'
                            }
                          }),
                          dropdownIndicator: (provided) => ({
                            ...provided,
                            color: '#9ca3af',
                            '&:hover': {
                              color: 'white'
                            }
                          })
                        }}
                      />
                      <hr className="border-black mt-2" />


                      {/* Switch to upload mode */}
                      <button
                        onClick={() => setDocumentModalMode('upload')}
                        className="w-full px-3 py-2 bg-blue-600/20 text-blue-400 hover:bg-blue-500 hover:text-white rounded-md text-xs transition-all duration-200 text-left"
                      >
                        Or upload new document
                      </button>
                    </>
                  )}

                  {/* Show upload form if in upload mode and not editing */}
                  {documentModalMode === 'upload' && !newDocument.id && (
                    <>
                      <input
                        type="text"
                        value={newDocument.name || ''}
                        onChange={(e) => setNewDocument((prev: any) => prev ? {...prev, name: e.target.value} : null)}
                        placeholder="Document Name"
                        className="w-full px-2 py-1.5 bg-[#082A43] border border-[#4d4d4d] rounded-md text-white placeholder-gray-500 text-sm focus:outline-none focus:border-blue-500"
                      />

                      {/* File Selection */}
                      <div className="relative">
                        <input
                          type="file"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setNewDocument((prev: any) => prev ? {...prev, file} : null);
                            }
                          }}
                          accept=".pdf"
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                        />
                        <div className="w-full px-2 py-1.5 bg-[#082A43] border border-[#4d4d4d] rounded-md text-white flex items-center gap-2">
                          <FiUpload className="text-gray-400" />
                          <span className="text-gray-400">
                            {newDocument.file ? newDocument.file.name : 'Choose PDF file'}
                          </span>
                        </div>
                      </div>

                      {/* Back to select button if documents exist */}
                      {availableDocuments.length > 0 && (
                        <button
                          onClick={() => {
                            setDocumentModalMode('select');
                            setSelectedDocumentId('');
                          }}
                          className="w-full px-3 py-2 bg-gray-600/20 text-gray-400 hover:bg-gray-500 hover:text-white rounded-md text-xs transition-all duration-200 text-left"
                        >
                          Back to document selection
                        </button>
                      )}
                    </>
                  )}
                </div>

                <div className="flex gap-2 mt-2">
                  <button
                    onClick={handleDocumentCancel}
                    className="flex-1 px-3 py-1.5 bg-blue-600/20 text-blue-400 hover:bg-blue-500 hover:text-white rounded-md text-sm transition-all duration-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      if (documentModalMode === 'select' && selectedDocumentId) {
                        // Add selected existing document
                        const selectedDoc = availableDocuments.find(doc => doc.id === selectedDocumentId);
                        if (selectedDoc) {
                          // Fetch the file content and create a File object
                          const fetchAndAddDocument = async () => {
                            try {
                              const response = await getFile(selectedDoc.fileId);
                              if (response.status === 200) {
                                // Convert base64 data to Uint8Array
                                const base64Data = response.data.data || response.data;
                                const binaryString = atob(base64Data);
                                const bytes = new Uint8Array(binaryString.length);
                                for (let i = 0; i < binaryString.length; i++) {
                                  bytes[i] = binaryString.charCodeAt(i);
                                }
                                
                                // Create blob and file from the decoded data
                                const blob = new Blob([bytes], { type: response.data.mimeType || 'application/pdf' });
                                const file = new File([blob], response.data.fileName || selectedDoc.fileName, { type: response.data.mimeType || 'application/pdf' });

                                // Create a document object from the selected document
                                const newDocumentObj: Document = {
                                  id: Date.now().toString(),
                                  x: newDocument.x,
                                  y: newDocument.y,
                                  name: selectedDoc.name,
                                  file: file, // Use File object instead of fileUrl
                                  fileId: selectedDoc.fileId,
                                  createdAt: new Date().toISOString()
                                };
                                setDocuments(prev => [...prev, newDocumentObj]);
                                setNewDocument(null);
                                setSelectedDocumentId('');
                                setDocumentModalMode('select');
                                setAddDocumentOpen(false);
                              }
                            } catch (error) {
                              console.error('Failed to fetch document:', error);
                            }
                          };
                          fetchAndAddDocument();
                        }
                      } else if (newDocument.id) {
                        // Handle editing existing document
                        const existingDoc = documents.find(doc => doc.id === newDocument.id);
                        const fileIdChanged = existingDoc && (newDocument as any).fileId !== existingDoc.fileId;
                        
                        if (fileIdChanged) {
                          // Fetch new file content
                          const fetchAndUpdateDocument = async () => {
                            try {
                              const response = await getFile((newDocument as any).fileId);
                              if (response.status === 200) {
                                // Convert base64 data to Uint8Array
                                const base64Data = response.data.data || response.data;
                                const binaryString = atob(base64Data);
                                const bytes = new Uint8Array(binaryString.length);
                                for (let i = 0; i < binaryString.length; i++) {
                                  bytes[i] = binaryString.charCodeAt(i);
                                }
                                
                                // Create blob and file from the decoded data
                                const blob = new Blob([bytes], { type: response.data.mimeType || 'application/pdf' });
                                const file = new File([blob], response.data.fileName || (newDocument as any).fileName, { type: response.data.mimeType || 'application/pdf' });

                                // Update document with new file and fileId
                                setDocuments(prev => prev.map(doc =>
                                  doc.id === newDocument.id
                                    ? { 
                                        ...doc, 
                                        name: newDocument.name,
                                        fileId: (newDocument as any).fileId,
                                        file: file
                                      }
                                    : doc
                                ));
                              }
                            } catch (error) {
                              console.error('Failed to fetch new document:', error);
                            }
                          };
                          fetchAndUpdateDocument();
                        } else {
                          // Just update name
                          setDocuments(prev => prev.map(doc =>
                            doc.id === newDocument.id
                              ? { 
                                  ...doc, 
                                  name: newDocument.name
                                }
                              : doc
                          ));
                        }
                        setNewDocument(null);
                        setAddDocumentOpen(false);
                      } else {
                        // Handle upload mode for new documents
                        if (!newDocument.name || !newDocument.file) return;
                        handleDocumentSubmit(newDocument.name, newDocument.file);
                      }
                    }}
                    disabled={
                      isUploadingDocument ||
                      (!newDocument.id && documentModalMode === 'select' && !selectedDocumentId) ||
                      (!newDocument.id && documentModalMode === 'upload' && (!newDocument.name || !newDocument.file))
                    }
                    className="px-3 py-1.5 bg-gray text-gray-400 hover:text-white rounded-md text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isUploadingDocument ? (
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 border-2 border-gray-400 border-t-white rounded-full animate-spin"></div>
                        <span>Uploading...</span>
                      </div>
                    ) : (
                      newDocument.id ? 'Update' : (documentModalMode === 'select' ? 'Add' : 'Upload')
                    )}
                  </button>
                </div>
              </div>
            )}

            {stats && (
              <div className="fixed bottom-4 right-4 text-white p-4 rounded shadow-lg bg-black/85 z-50">
                <div>Total Entities: {stats.totalEntities}</div>
                <div>Drawn Entities: {stats.drawnEntities}</div>
                <div>Scale: {(stats.scale || 1).toFixed(2)}x</div>
                <div>Comments: {comments.length}</div>
                <div>Sensors: {labels.length}</div>
              </div>
            )}
          </>
        )}
      </div>

      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(-4px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes fadeOut {
            from {
              opacity: 1;
              transform: translateY(0);
            }
            to {
              opacity: 0;
              transform: translateY(-4px);
            }
          }
          
          .animate-fade-in {
            animation: fadeIn 0.3s ease-out forwards;
          }
          
          .animate-fade-out {
            animation: fadeOut 0.3s ease-out forwards;
          }
        `}
      </style>
      {/* Clear confirmation modal */}
      {showClearModal && (
        <Dialog.Root open={showClearModal} onOpenChange={setShowClearModal}>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 bg-black/60 z-50" />
            <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#1B1B1B] p-6 rounded-md border border-lightGray w-[320px] text-white z-50">
              <Dialog.Title className="mb-4 text-lg">Clear DXF Data?</Dialog.Title>
              <p className="mb-6 text-sm text-gray-300">This will remove the drawing and all comments for this tab. Are you sure?</p>
              <div className="flex justify-end gap-3">
                <button className="btn-sec" onClick={() => setShowClearModal(false)}>No</button>
                <button className="btn-danger" onClick={clearDataConfirmed}>Yes</button>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}
      {itemActionMenu && (
        <div
          ref={itemActionRef}
          className="absolute bg-[#082A43] rounded-md shadow-xl py-1 w-32 text-xs"
          style={{ 
            left: itemActionMenu.screenX, 
            top: itemActionMenu.screenY,
            zIndex: 1010 // Higher than document layer's z-index (999-1000)
          }}
        >
          <button
            className="w-full px-3 py-1 text-left text-gray-300 hover:bg-gray rounded-md transition-colors"
            onClick={() => {
              if (itemActionMenu.type === 'comment') {
                const c = comments.find(c => c.id === itemActionMenu.id);
                if (c) {
                  setNewComment({ x: c.x, y: c.y, id: c.id, screenX: itemActionMenu.screenX, screenY: itemActionMenu.screenY });
                  setCommentText(c.text);
                  setIsEditingComment(true);
                }
              } else if (itemActionMenu.type === 'label') {
                const l = labels.find(l => l.id === itemActionMenu.id);
                if (l) {
                  setNewLabel({ x: l.x, y: l.y, id: l.id, tagId: l.tagId, expressionId: l.expressionId, screenX: itemActionMenu.screenX, screenY: itemActionMenu.screenY });
                  setLabelInput(l.label);
                  setSelectedSource(l.source);
                  if (l.source === 'tag') setSelectedTagId(l.tagId ?? 0);
                  else setSelectedExpressionId(l.expressionId ?? 0);
                  setIsEditingLabel(true);
                }
              } else if (itemActionMenu.type === 'widget') {
                // Find the widget to edit
                const widgetToEdit = widgets.find(w => w.id === itemActionMenu.id);
                if (widgetToEdit) {
                  setEditingWidget(widgetToEdit);
                  setAddWidgetOpen(true);
                }
              } else if (itemActionMenu.type === 'document') {
                const d = documents.find(d => d.id === itemActionMenu.id);
                if (d) {
                  setNewDocument({ x: d.x, y: d.y, id: d.id, documentId: Number(d.id), name: d.name, screenX: itemActionMenu.screenX, screenY: itemActionMenu.screenY, file: d.file, fileId: (d as any).fileId });
                  setAddDocumentOpen(true);
                }
              }
              setItemActionMenu(null);
            }}
          >Edit</button>
          <button
            className="w-full px-3 py-1 text-left text-gray-300 hover:bg-gray rounded-md transition-colors"
            onClick={() => {
              // Check if view state is properly initialized before allowing move operations
              const view = viewState.current;
              if (!view.bounds || typeof view.bounds.minX !== 'number' || typeof view.bounds.minY !== 'number') {
                setItemActionMenu(null);
                return;
              }
              
              setMovingItem({ type: itemActionMenu.type, id: itemActionMenu.id });
              setItemActionMenu(null);
            }}
          >Move</button>
        
          
        
          <button
            className="w-full px-3 py-1 text-left text-gray-300 hover:bg-gray rounded-md transition-colors"
            onClick={() => {
              // start resize mode
              const start = Number.NaN; // no drag yet
              let initialScale = 1;
              if (itemActionMenu.type === 'comment') {
                const c = comments.find(c => c.id === itemActionMenu.id);
                initialScale = c?.scale ?? 1;
              } else if (itemActionMenu.type === 'label') {
                const l = labels.find(l => l.id === itemActionMenu.id);
                initialScale = l?.scale ?? 1;
              } else if (itemActionMenu.type === 'widget') {
                // Widget scale will be handled by the WidgetLayer component
                // For now, use default scale
                initialScale = 1;
              } else if (itemActionMenu.type === 'document') {
                const d = documents.find(d => d.id === itemActionMenu.id);
                initialScale = d?.scale ?? 1;
                setResizingItem({ type: itemActionMenu.type, id: itemActionMenu.id, startX: start, initialScale });
                setIsDocumentResizing(true);
                setDocumentResizingId(itemActionMenu.id);
                setItemActionMenu(null);
                draw();
                return;
              }
              setResizingItem({ type: itemActionMenu.type, id: itemActionMenu.id, startX: start, initialScale });
              setIsWidgetResizing(true);
              setWidgetResizingId(itemActionMenu.id);
              setItemActionMenu(null);
              draw();
            }}
          >Resize</button>
          <button
            className="w-full px-3 py-1 text-left text-gray-300 hover:bg-gray rounded-md transition-colors"
            onClick={() => {
              if (itemActionMenu.type === 'document') {
                const doc = documents.find(d => d.id === itemActionMenu.id);
                if (doc && doc.file instanceof File) {
                  const url = URL.createObjectURL(doc.file);
                  window.open(url, '_blank');
                  // Clean up the URL after opening
                  URL.revokeObjectURL(url);
                }
              }
              setItemActionMenu(null);
            }}
          >Open Document</button>
          <button
            className="w-full px-3 py-1 text-left text-red-400 hover:bg-gray rounded-md transition-colors"
            onClick={() => {
              setPendingDelete({ type: itemActionMenu.type, id: itemActionMenu.id });
              setItemActionMenu(null);
            }}
          >Delete</button>
          {/* Commented out - Fix Size functionality to be removed later
          <button
              className="w-full px-3 py-1 text-left text-red-400 hover:bg-gray rounded-md transition-colors"
            onClick={() => {
              if (itemActionMenu.type === 'comment') {
                setComments(prev => prev.map(c => c.id === itemActionMenu.id ? { ...c, fixedSize: !c.fixedSize } : c));
              } else if (itemActionMenu.type === 'label') {
                setLabels(prev => {
                  const updated = prev.map(l => l.id === itemActionMenu.id ? { ...l, fixedSize: !l.fixedSize } : l);
                  labelsRef.current = updated;
                  return updated;
                });
              } else if (itemActionMenu.type === 'widget') {
                // Handle widget fixed size
                const widget = widgets.find(w => w.id === itemActionMenu.id);
                if (widget) {
                  updateWidgetFixedSize(itemActionMenu.id, !widget.fixedSize);
                }
              } else if (itemActionMenu.type === 'document') {
                // Handle document fixed size
                setDocuments(prev => prev.map(d => d.id === itemActionMenu.id ? { ...d, fixedSize: !d.fixedSize } : d));
              }
              setItemActionMenu(null);
            }}
          >
            <span>{
              (itemActionMenu.type === 'comment'
                ? comments.find(c => c.id === itemActionMenu.id)?.fixedSize
                : itemActionMenu.type === 'label'
                ? labels.find(l => l.id === itemActionMenu.id)?.fixedSize
                : itemActionMenu.type === 'widget'
                ? widgets.find(w => w.id === itemActionMenu.id)?.fixedSize
                : itemActionMenu.type === 'document'
                ? documents.find(d => d.id === itemActionMenu.id)?.fixedSize
                : false
              ) ? <span style={{ color: '#fff', marginRight: 6 }}>✓</span> : <span style={{ color: '#fff'}}></span>}</span>
            Fix size
          </button>
          */}
        </div>
      )}
      {/* Delete confirmation modal */}
      {pendingDelete && (
        <Dialog.Root open={true} onOpenChange={(open) => { if (!open) setPendingDelete(null); }}>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 bg-black/60 z-50" />
            <Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#1B1B1B] p-6 rounded-md border border-lightGray w-[320px] text-white z-50">
              <Dialog.Title className="mb-4 text-lg">Delete {pendingDelete.type === 'label' ? 'Sensor' : pendingDelete.type === 'widget' ? 'Widget' : pendingDelete.type === 'document' ? 'Document' : 'Comment'}?</Dialog.Title>
              <p className="mb-6 text-sm text-gray-300">Are you sure you want to delete this {pendingDelete.type === 'label' ? 'sensor' : pendingDelete.type === 'widget' ? 'widget' : pendingDelete.type === 'document' ? 'document' : 'comment'}? This action cannot be undone.</p>
              <div className="flex justify-end gap-3">
                <button className="btn-sec" onClick={() => setPendingDelete(null)}>No</button>
                <button className="btn-danger" onClick={() => {
                  if (pendingDelete.type === 'comment') {
                    setComments(prev => prev.filter(c => c.id !== pendingDelete.id));
                  } else if (pendingDelete.type === 'label') {
                    setLabels(prev => {
                      const updated = prev.filter(l => l.id !== pendingDelete.id);
                      labelsRef.current = updated;
                      return updated;
                    });
                  } else if (pendingDelete.type === 'widget') {
                    removeWidget(pendingDelete.id);
                  } else if (pendingDelete.type === 'document') {
                    setDocuments(prev => prev.filter(d => d.id !== pendingDelete.id));
                  }
                  setPendingDelete(null);
                  draw();
                }}>Yes</button>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}

      {/* Toast Notification */}
      {toastMessage && (
        <div
          className={`fixed left-1/2 transform -translate-x-1/2 z-[9999] transition-all duration-300 ease-in-out ${
            toastVisible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'
          }`}
          style={{
            top: hasDxfData ? '135px' : '80px', // Position below action bar when DXF data is present
            background: '#0c3756', // Match widget modal background
            border: '1px solid #374151', // Match widget modal border
            borderRadius: '8px', // Match widget modal border radius
            padding: '12px 20px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
            maxWidth: '400px',
            minWidth: '300px'
          }}
        >
          <div className="flex items-center gap-3">
            <div className="text-white text-sm font-medium">
              {toastMessage}
            </div>
            {!toastPersistent && (
              <button
                onClick={() => {
                  setToastVisible(false);
                  setTimeout(() => setToastMessage(null), 300);
                }}
                className="text-white/70 hover:text-white transition-colors ml-2"
                style={{ fontSize: '18px', lineHeight: '1' }}
              >
                ×
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default DxfCanvasViewer;

